// /src/components/topLevelNav/index.js
import React, { Component } from "react";

import "bootstrap/dist/css/bootstrap.css";
import { connect } from "react-redux";
import {
  setSelectedTab,
  removeTabFromNav,
  removeChild,
  addTabToNav,
  filterTab,
} from "../../components/topLevelNav/reducers/topLevelNavReducer";
import { setReportGroup, setCustomGroup, setReportAppStatus } from "../../reducers/appReducer";
import { clearFileSearch } from "../../components/files/reducers/filesReducer";
import {
  setBreadcrumbSelection,
  clearBreadcrumbSelection,
  pushBreadcrumbItem,
  insertBreadcrumbItemAfter,
  removeBreadcrumbItem,
  popBreadcrumbToId,
  clearBreadcrumbStack,
} from "../../reducers/breadcrumbReducer";
import { Icon } from "react-icons-kit";
import { home3 } from "react-icons-kit/icomoon/home3";
import TopLevelParentNavItem from "../../components/topLevelNav/components/topLevelParentNavItem";
import { CloseReportIcon } from "../../lib/icons";
import { encodePathKey, getReportingIndex, getRouteContext, getRouteSelection } from "../../lib/reportingNavigation";
import { getWsParentFolderPath, buildAncestorFolderPaths, resolveWsFolderStackId } from "../../lib/breadcrumbNavigation";
import "./style.css";
import { cloneDeep } from "lodash";
import withRouter from "../../hocs/withRouter";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

// ── Tiny inline SVG icons for breadcrumb chip ────────────────────────────────
const _bcIconStyle = { display: "inline", verticalAlign: "middle", flexShrink: 0 };
const BcHomeIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={_bcIconStyle}>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const BcFolderIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={_bcIconStyle}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);
const BcSubfolderIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={_bcIconStyle}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <line x1="8" y1="13" x2="16" y2="13" strokeDasharray="2 2" />
  </svg>
);
const BcDocIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={_bcIconStyle}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
// ─────────────────────────────────────────────────────────────────────────────
// Span that applies marginRight: -6px only when its text is actually truncated.
function BcLabelText({ children, applyFix }) {
  const outerRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  React.useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || !applyFix) return;
    // IMPORTANT: do NOT observe inner or compare inner.clientWidth.
    // Applying marginRight:-6px changes inner's clientWidth -> re-triggers observer
    // -> isTruncated flips -> margin removed -> shrinks -> truncated -> infinite loop.
    // Fix: observe OUTER (flex-sized, immune to our style) and compare
    // inner.scrollWidth (raw text width) vs outer.clientWidth (available width).
    const check = () => setIsTruncated(inner.scrollWidth > outer.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [children, applyFix]);
  return (
    <span ref={outerRef} className="bc-label-text" data-text={typeof children === "string" ? children : undefined}>
      <span ref={innerRef} className="bc-label-inner" style={applyFix && isTruncated ? { marginRight: "-6px" } : undefined}>
        {children}
      </span>
    </span>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export class TopLevelNav extends Component {
  constructor(props) {
    super(props);
    this.clickHandler = this.clickHandler.bind(this);
    this.homeClickHandler = this.homeClickHandler.bind(this);
    this.renderSubTabs = this.renderSubTabs.bind(this);
    this.childRemoveHandler = this.childRemoveHandler.bind(this);
    this.closeMenuReport = this.closeMenuReport.bind(this);
    this.handleTabClose = this.handleTabClose.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleDerivedBreadcrumbClose = this.handleDerivedBreadcrumbClose.bind(this);
    this.navRef = React.createRef();
    this.state = {
      showClose: -1,
      // Tracks a just-saved report ID while workspaces reload after creation.
      // Set on the "new" → real-ID URL transition; cleared once the report appears in the index.
      savingReportId: "",
    };
  }

  matchesRouteSelection(node, routeSelection) {
    if (!node || !routeSelection) {
      return false;
    }
    const { groupId, folderPath, reportId } = routeSelection;
    if (reportId) {
      const nodeId = node.id || node._id || node.rptId || "";
      if (nodeId && nodeId === reportId) {
        return true;
      }
      if (node.id && node.id.includes("+") && node.id.split("+")[1] === reportId) {
        return true;
      }
    }
    if (groupId && (node.id === groupId || node._id === groupId)) {
      return true;
    }
    if (folderPath && node.path && encodePathKey(node.path) === encodePathKey(folderPath)) {
      return true;
    }
    return false;
  }

  activeChildrenMatchRoute(activeChildren, routeSelection) {
    if (!Array.isArray(activeChildren)) {
      return false;
    }
    const stack = [...activeChildren];
    while (stack.length > 0) {
      const node = stack.pop();
      if (this.matchesRouteSelection(node, routeSelection)) {
        return true;
      }
      if (Array.isArray(node?.children)) {
        stack.push(...node.children);
      }
      if (Array.isArray(node?.activeChildren)) {
        stack.push(...node.activeChildren);
      }
    }
    return false;
  }

  // Recursively checks whether reportId appears anywhere under a workspace/tab's
  // report containers (root-level reports, nested groupings, or the folder tree).
  // Tab entries store root reports under `children` while raw workspace objects use
  // `reports`, so both are checked. Shared by componentDidUpdate (decides which app's
  // stack to push the report into) and render (decides which app's stack to display) —
  // they must agree, or the wrong app's tab can end up paired with an empty stack.
  workspaceOwnsReport(ws, reportId) {
    const matchesId = (node) => node?.rptId === reportId || node?.id === reportId || node?._id === reportId;
    const walk = (nodes) =>
      Array.isArray(nodes) && nodes.some((node) => matchesId(node) || walk(node?.children) || walk(node?.activeChildren));
    return walk(ws?.reports) || walk(ws?.groupings) || walk(ws?.fileGroupings) || walk(ws?.children);
  }

  // A workspace object is a "custom app" if it's explicitly typed as one, or carries
  // groupings (custom apps group reports into named collections; plain PBI workspaces
  // don't use this field). Shared by every custom-app-workspace lookup so the definition
  // can't drift between call sites.
  isCustomApp(ws) {
    return ws?.type === "customApplication" || (ws?.groupings?.length ?? 0) > 0;
  }

  // Resolves a ?appId= against `pool` (typically [...workspaces, ...tabs]) and, when
  // reportId is given, verifies that specific match actually owns the report before
  // accepting it. Matches on id first, then validates — it does NOT keep scanning for
  // a different same-id entry if the first match fails ownership. That matters because
  // `workspaces` (fresh) and `tabs` (a snapshot taken once at addTabToNav time, per
  // workspaceOwnsReport's comment above) can both contain an entry for the same app
  // with different data; scanning past a failed match risks silently accepting a stale
  // tab snapshot that still (incorrectly) appears to own a report the fresh workspace
  // data says it no longer does. Shared by componentDidUpdate and render so the write
  // side and read side can't drift into applying this check differently (BIG-1704).
  resolveValidatedAppWorkspace(pool, appId, reportId) {
    if (!appId) return null;
    const appWs = (pool || []).find((w) => this.isCustomApp(w) && (w?._id === appId || w?.workspaceId === appId));
    if (!appWs) return null;
    if (reportId && !this.workspaceOwnsReport(appWs, reportId)) return null;
    return appWs;
  }

  // Called once componentDidUpdate's other custom-app fallbacks have all failed to place
  // this report in a custom app (i.e. ?appId= names a real app that just doesn't own this
  // report). In that situation CDU would otherwise fall through to the report's actual
  // owning workspace — which is fine, unless that workspace is hidden. Hidden workspaces
  // aren't meant to be navigated into directly, so routing there reads as broken/confusing
  // rather than as a fallback. Only kicks in when EVERY regular (non-custom-app) workspace
  // that owns the report is hidden — if even one visible workspace also owns it, the normal
  // fallback below is correct and this returns null. Returns the app's canonical id to
  // redirect the user to (its App Home), or null if the redirect doesn't apply.
  resolveHiddenOnlyOwnerAppHomeId(pool, workspaces, appId, reportId) {
    if (!appId || !reportId) return null;
    const appWs = (pool || []).find((w) => this.isCustomApp(w) && (w?._id === appId || w?.workspaceId === appId));
    if (!appWs) return null;
    const owningWorkspaces = (workspaces || []).filter((ws) => !this.isCustomApp(ws) && this.workspaceOwnsReport(ws, reportId));
    if (owningWorkspaces.length === 0 || owningWorkspaces.some((ws) => !ws?.hidden)) return null;
    return appWs._id || appWs.workspaceId;
  }

  handleDerivedBreadcrumbClose(routeSelection) {
    const workspaceId = routeSelection?.workspaceId || "";
    if (!workspaceId) {
      return;
    }
    const context = getRouteContext(this.props.location?.pathname || "", this.props.workspaces || []);
    const reportKey = routeSelection?.reportId || "";
    const reportEntry = reportKey ? context?.index?.nodeById?.[reportKey] : null;
    const reportWorkspace = reportEntry?.workspace || null;
    const findCustomAppForReport = (reportId, workspaces = []) => {
      if (!reportId || !Array.isArray(workspaces)) {
        return null;
      }
      for (const ws of workspaces) {
        if (ws?.type !== "customApplication" || !Array.isArray(ws.groupings)) {
          continue;
        }
        for (const group of ws.groupings) {
          const children = Array.isArray(group?.children) ? group.children : [];
          if (children.some((child) => child?.id === reportId || child?._id === reportId || child?.rptId === reportId)) {
            return ws;
          }
        }
      }
      return null;
    };
    const customAppWorkspace = findCustomAppForReport(reportKey, this.props.workspaces || []);
    const breadcrumbWorkspaceId =
      customAppWorkspace?._id ||
      (reportWorkspace?.type === "customApplication"
        ? reportWorkspace?._id
        : context?.workspace?.workspaceId || context?.workspace?._id || workspaceId);
    this.props.clearBreadcrumbSelection(breadcrumbWorkspaceId);
    const tabIndex = this.props.tabs.findIndex(
      (tab) =>
        tab?.workspaceId === breadcrumbWorkspaceId || tab?._id === breadcrumbWorkspaceId || tab?.id === breadcrumbWorkspaceId,
    );
    if (tabIndex === -1) {
      this.props.setCustomGroup("");
      this.props.setReportGroup(breadcrumbWorkspaceId);
      this.props.navigate(`/reports/landing/${breadcrumbWorkspaceId}`, { replace: true });
      return;
    }

    const tab = this.props.tabs[tabIndex];
    const siblings = Array.isArray(tab?.activeChildren) ? tab.activeChildren : [];
    const matchesRoute = (item) => {
      if (!item) {
        return false;
      }
      if (routeSelection?.groupId && (item.id === routeSelection.groupId || item._id === routeSelection.groupId)) {
        return true;
      }
      if (routeSelection?.folderPath && item.path) {
        return encodePathKey(item.path) === encodePathKey(routeSelection.folderPath);
      }
      return false;
    };
    const routeIdx = siblings.findIndex(matchesRoute);
    const leftIdx = routeIdx > 0 ? routeIdx - 1 : siblings.length - 1;
    const leftItem = leftIdx >= 0 ? siblings[leftIdx] : null;

    if (leftItem) {
      const leftType = leftItem.type || (leftItem.fileType === "file" ? "powerBI" : leftItem.fileType);
      this.props.setSelectedTab([tabIndex, leftIdx]);
      this.clickHandler(leftItem, [tabIndex, leftIdx], leftType, false);
      return;
    }

    this.props.setSelectedTab([tabIndex, -1]);
    this.props.setCustomGroup("");
    this.props.setReportGroup(breadcrumbWorkspaceId);
    this.props.navigate(`/reports/landing/${breadcrumbWorkspaceId}`, { replace: true });
  }

  breadcrumbTestId(item) {
    if (!item) {
      return undefined;
    }
    const isGuid = (value) => typeof value === "string" && value.length === 36;
    const preferredId =
      (isGuid(item.rptId) && item.rptId) ||
      (isGuid(item.id) && item.id) ||
      (isGuid(item._id) && item._id) ||
      item.rptId ||
      item.id ||
      item._id ||
      item.path ||
      "";
    const raw = preferredId;
    if (!raw) {
      return undefined;
    }
    const safe = String(raw).replace(/[^a-zA-Z0-9-]/g, "_");
    return `breadcrumb-item-${safe}`;
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const prevPath = prevProps.location?.pathname || "";
    const currentPath = this.props.location?.pathname || "";

    // When getReports completes after a new-report save, workspaces update without a
    // URL change.  Clear savingReportId before any early-return so the BC label
    // transitions from "Saving..." to the real report name as soon as it's indexed.
    if (this.state.savingReportId) {
      if (getReportingIndex(this.props.workspaces || [])?.nodeById?.[this.state.savingReportId]) {
        this.setState({ savingReportId: "" });
      }
    }

    // Detect an unindexed report that was eagerly pushed into the BC stack before
    // navigation (e.g. clone-report workflow).  Set savingReportId immediately so
    // the BC label shows "Saving..." from the very first render rather than the GUID.
    // This runs before the path-change guard so it fires on BC-stack-only prop updates.
    if (!this.state.savingReportId && (this.props.workspaces?.length ?? 0) > 0) {
      const detIdx = getReportingIndex(this.props.workspaces || []);
      if (detIdx) {
        const allStacks = this.props.breadcrumbStacks || {};
        for (const wsStack of Object.values(allStacks)) {
          const unindexed = (wsStack || []).find(
            (item) => item.type === "report" && item.id !== "new" && !detIdx.nodeById?.[item.id],
          );
          if (unindexed) {
            this.setState({ savingReportId: unindexed.id });
            break;
          }
        }
      }
    }

    // Allow the update through when workspaces first become available so that a
    // hard reload (where prevPath === currentPath) still populates the BC stack.
    const workspacesJustLoaded =
      (!prevProps.workspaces || prevProps.workspaces.length === 0) && (this.props.workspaces?.length ?? 0) > 0;
    if (prevPath === currentPath && !workspacesJustLoaded) {
      return;
    }
    const selection = getRouteSelection(currentPath);
    const prevSelection = getRouteSelection(prevPath);

    // Hard-reload on a workspace app home: workspaces just arrived, no navigation
    // happened, so setReportGroup (which triggers the recents/favourites fetch) was
    // never called.  Mirror what appClickHandler does: reset reportAppLoaded first
    // so that reportIconList triggers getReports, then sets getFavoritesAndRecents.
    if (
      workspacesJustLoaded &&
      selection.workspaceId &&
      selection.isLanding &&
      !selection.groupId &&
      !selection.folderPath &&
      !selection.reportId
    ) {
      this.props.setReportAppStatus(false);
      this.props.setReportGroup(selection.workspaceId);
      return;
    }
    const workspaces = this.props.workspaces || [];
    const tabs = this.props.tabs || [];
    const selectedTab = this.props.selectedTab || [];
    const reportingIndex = getRouteContext(currentPath, workspaces)?.index;
    const markReportAsSavingIfPending = () => {
      if (!selection?.reportId) {
        return;
      }
      if (!prevSelection?.reportId || prevSelection.reportId === selection.reportId) {
        return;
      }
      const reportIsIndexed = Boolean(reportingIndex?.nodeById?.[selection.reportId]);
      if (!reportIsIndexed && this.state.savingReportId !== selection.reportId) {
        this.setState({ savingReportId: selection.reportId });
      }
    };
    const activeTab = tabs?.[selectedTab?.[0]] || null;
    // A custom-app tab may not carry type:"customApplication" directly; also check its
    // workspaceId against the workspaces list and the live breadcrumb stacks.
    const activeCustomAppId = (() => {
      if (!activeTab) return "";
      if (activeTab.type === "customApplication") return activeTab._id || activeTab.workspaceId || "";
      const wsId = activeTab.workspaceId || activeTab._id;
      if (!wsId) return "";
      // Check against workspaces list
      const matchedWs = [...workspaces, ...tabs].find(
        (w) => w?.type === "customApplication" && (w._id === wsId || w.workspaceId === wsId),
      );
      if (matchedWs) return matchedWs._id || matchedWs.workspaceId || "";
      // Fall back: a live stack exists for this wsId AND the workspace is actually a
      // custom app (has type:"customApplication" or groupings).
      // PBI workspace tabs also push a context stack now — filtering them out prevents
      // the PBI-workspace block from being skipped when the stack is non-empty.
      const stacks = this.props.breadcrumbStacks || {};
      const allPool = [...workspaces, ...tabs];
      const isActuallyCustomApp = allPool.some(
        (w) =>
          (w?._id === wsId || w?.workspaceId === wsId) && (w?.type === "customApplication" || (w?.groupings?.length ?? 0) > 0),
      );
      if (isActuallyCustomApp && stacks[wsId]?.length) return wsId;
      return "";
    })();

    // ── Helpers ───────────────────────────────────────────────────────────────
    const findWorkspaceByAnyId = (id, pools = []) =>
      pools.find((ws) => ws?._id === id || ws?.workspaceId === id || ws?.id === id) || null;

    /** Return the custom-app workspace _id that owns the given reportId, or null. */
    const resolveCustomAppWsIdForReport = (reportId) => {
      if (!reportId) return null;
      for (const ws of [...workspaces, ...tabs]) {
        if ((ws?.type !== "customApplication" && !(ws?.groupings?.length > 0)) || !Array.isArray(ws.groupings)) continue;
        for (const group of ws.groupings) {
          if ((group?.children || []).some((c) => c.rptId === reportId || c.id === reportId || c._id === reportId)) {
            return ws._id || ws.workspaceId;
          }
        }
      }
      return null;
    };

    // ── Determine whether this navigation belongs to a custom app ──────────────
    const prevWsEntry =
      findWorkspaceByAnyId(prevSelection?.workspaceId, workspaces) || findWorkspaceByAnyId(prevSelection?.workspaceId, tabs);

    // 4th fallback: the current URL itself targets a custom app (e.g. cold goto or
    // direct link to /reports/landing/<customAppId>/<groupId>).  Applies when the
    // previous page was unrelated (e.g. "/") so prevWsEntry is null and there is no
    // active tab yet.
    const currentWsEntry = findWorkspaceByAnyId(selection.workspaceId, workspaces);

    // 3rd fallback: prevWsEntry was a custom app.  Only use this when the active tab
    // has NOT been switched to a different workspace.  If setSelectedTab already moved
    // the active tab to a different workspace (e.g. the user clicked a report from a
    // non-active workspace's dropdown), the navigation belongs to that workspace and
    // we must NOT route the BC push to the custom app's stack.
    const prevWsEntryCustomAppFallback = (() => {
      if (prevWsEntry?.type !== "customApplication") return null;
      const prevCustomAppId = prevWsEntry._id || prevWsEntry.workspaceId;
      if (activeTab) {
        const activeWsId = activeTab.workspaceId || activeTab._id;
        if (activeWsId && activeWsId !== prevCustomAppId) return null;
      }
      return prevCustomAppId;
    })();

    // resolveCustomAppWsIdForReport looks up a report GUID across ALL custom-app
    // groupings.  Because the same PBI GUID can exist in both a regular workspace and a
    // custom app, calling it unconditionally would mis-route BC pushes to the custom
    // app's stack even when the user is viewing the report inside the workspace.
    //
    // Skip it when the active tab already points to a regular (non-custom-app) workspace —
    // identified by type being falsy/undefined (the isPowerBIWsTab path) OR by the active
    // tab's workspaceId matching the workspace in the current URL.  In both cases the
    // active tab provides the correct context and the custom-app lookup is not needed.
    // Keep it for cold-reload / deep-link (no active tab) and for the custom-app case
    // (activeCustomAppId is set), where it still acts as the primary resolver.
    const activeTabWsId = activeTab ? activeTab.workspaceId || activeTab._id || "" : "";
    const activeTabIsRegularWorkspace =
      activeTab && !activeCustomAppId && (!activeTab.type || activeTabWsId === selection.workspaceId);
    const resolvedCustomAppId = activeTabIsRegularWorkspace ? null : resolveCustomAppWsIdForReport(selection.reportId);

    // When ?appId=<mongoId> is present in the URL it was explicitly written by
    // appLanding.js to signal that the current report lives inside a custom app.
    // Use it as a reliable fallback when resolveCustomAppWsIdForReport can't find a
    // match (e.g. the groupings children use a different ID field). A stale ?appId
    // left over from a previous report (browser back/forward, a hand-edited URL)
    // must not hijack breadcrumb routing to the wrong app — resolveValidatedAppWorkspace
    // rejects it outright if the report isn't actually owned by that specific app.
    const urlAppId = new URLSearchParams(this.props.location?.search || "").get("appId") || null;
    const routeCustomAppWsId = (() => {
      if (!urlAppId || !selection.reportId) return null;
      const appWs = this.resolveValidatedAppWorkspace([...workspaces, ...tabs], urlAppId, selection.reportId);
      return appWs ? appWs._id || appWs.workspaceId : null;
    })();

    const customAppWsId =
      routeCustomAppWsId ||
      resolvedCustomAppId ||
      activeCustomAppId ||
      prevWsEntryCustomAppFallback ||
      (currentWsEntry?.type === "customApplication" || (currentWsEntry?.groupings?.length ?? 0) > 0
        ? currentWsEntry._id || currentWsEntry.workspaceId
        : null);

    // None of the above resolved a custom app for this report, i.e. ?appId= names an app
    // that doesn't actually own it. Check whether the fallback below is about to route the
    // user into a hidden workspace — see resolveHiddenOnlyOwnerAppHomeId for why that's
    // redirected to the app's home instead.
    if (!customAppWsId) {
      const hiddenOnlyOwnerAppHomeId = this.resolveHiddenOnlyOwnerAppHomeId(
        [...workspaces, ...tabs],
        workspaces,
        urlAppId,
        selection.reportId,
      );
      if (hiddenOnlyOwnerAppHomeId) {
        const appTabIdx = tabs.findIndex((t) => t?.workspaceId === hiddenOnlyOwnerAppHomeId || t?._id === hiddenOnlyOwnerAppHomeId);
        if (appTabIdx !== -1) {
          this.props.setSelectedTab([appTabIdx, -1]);
        }
        this.props.setCustomGroup("");
        this.props.setReportGroup(hiddenOnlyOwnerAppHomeId);
        this.props.navigate(`/reports/landing/${hiddenOnlyOwnerAppHomeId}`, { replace: true });
        return;
      }
    }

    if (customAppWsId) {
      // App Home (bare landing, no group/report) — keep stack unchanged.
      if (selection.isLanding && !selection.groupId && !selection.folderPath && !selection.reportId) {
        return;
      }

      const allWsPool = [...workspaces, ...tabs];

      /**
       * Resolve the canonical store key — the key the live stack is actually
       * stored under.  Prefer the key that already has data so we never create
       * a second stack for the same workspace under a different ID variant.
       */
      const resolveCanonicalWsId = (wsId) => {
        const stacks = this.props.breadcrumbStacks || {};
        if (stacks[wsId]?.length) return wsId; // direct hit
        const appWs = allWsPool.find((w) => w?.type === "customApplication" && (w._id === wsId || w.workspaceId === wsId));
        if (!appWs) return wsId;
        if (stacks[appWs._id]?.length) return appWs._id;
        if (stacks[appWs.workspaceId]?.length) return appWs.workspaceId;
        // No existing stack yet — use _id as canonical for consistency.
        return appWs._id || appWs.workspaceId || wsId;
      };

      const canonicalWsId = resolveCanonicalWsId(customAppWsId);
      const currentStack = (this.props.breadcrumbStacks || {})[canonicalWsId] || [];

      if (selection.reportId) {
        markReportAsSavingIfPending();

        // Report already tracked in the stack — navigation only, no state change.
        // Also check cross-ID: if the stack has the PBI GUID but the URL was built
        // with the Mongo _id (or vice versa), resolve both IDs before deciding.
        const isReportInStack = (reportId) => {
          if (currentStack.some((item) => item.id === reportId)) return true;
          // Look up the canonical PBI GUID for this reportId via the reporting index.
          const node = reportingIndex?.nodeById?.[reportId]?.node;
          if (node) {
            const altIds = [node.rptId, node._id, node.id].filter(Boolean);
            if (altIds.some((altId) => currentStack.some((item) => item.id === altId))) return true;
          }
          return false;
        };
        if (isReportInStack(selection.reportId)) {
          return;
        }

        // Register the custom-app tab if it doesn't exist yet (e.g. on reload when
        // tabs are empty but the URL tells us this report belongs to a custom app).
        const tabAlreadyExistsForReport = tabs.some((t) => t?.workspaceId === canonicalWsId || t?._id === canonicalWsId);
        if (!tabAlreadyExistsForReport) {
          const appWsObjForTab = allWsPool.find(
            (w) =>
              (w?.type === "customApplication" || (w?.groupings?.length ?? 0) > 0) &&
              (w._id === canonicalWsId || w.workspaceId === canonicalWsId),
          );
          if (appWsObjForTab) {
            this.props.addTabToNav({
              title: appWsObjForTab.name,
              workspaceName: appWsObjForTab.name,
              workspaceId: appWsObjForTab._id || appWsObjForTab.workspaceId,
              children: appWsObjForTab.children,
              activeChildren: [],
              icon: appWsObjForTab.icon || appWsObjForTab.type,
              type: appWsObjForTab.type,
              groupings: appWsObjForTab.groupings,
              applications: appWsObjForTab.applications,
              order: appWsObjForTab.order,
              alias: appWsObjForTab.alias,
            });
          }
        }

        // "new" → real ID transition: the user just saved a newly created report.
        // Remove the "new" placeholder and push the real report ID.  The label will
        // show "Saving..." until getReports completes and the report is indexed.
        if (prevSelection.reportId === "new" && selection.reportId !== "new") {
          this.props.removeBreadcrumbItem(canonicalWsId, "new");
          this.props.pushBreadcrumbItem(canonicalWsId, { type: "report", id: selection.reportId });
          this.setState({ savingReportId: selection.reportId });
          return;
        }

        // Find which grouping in this custom app owns the report.
        const appWs = allWsPool.find(
          (w) =>
            (w?.type === "customApplication" || (w?.groupings?.length ?? 0) > 0) &&
            (w._id === canonicalWsId || w.workspaceId === canonicalWsId),
        );
        let parentGroup = null;
        if (appWs?.groupings) {
          for (const group of appWs.groupings) {
            if (
              (group?.children || []).some(
                (c) => c.rptId === selection.reportId || c.id === selection.reportId || c._id === selection.reportId,
              )
            ) {
              parentGroup = group;
              break;
            }
          }
        }
        if (parentGroup) {
          const groupId = parentGroup.id || parentGroup._id;
          // Build the set of all IDs that belong to this group (group itself + all child report IDs).
          const groupMemberIds = new Set([groupId]);
          (parentGroup.children || []).forEach((c) => {
            if (c.rptId) groupMemberIds.add(c.rptId);
            if (c.id) groupMemberIds.add(c.id);
            if (c._id) groupMemberIds.add(c._id);
          });

          if (groupId && !currentStack.some((item) => item.id === groupId)) {
            // Group not in stack yet — push group then report normally.
            this.props.pushBreadcrumbItem(canonicalWsId, { type: "group", id: groupId });
            this.props.pushBreadcrumbItem(canonicalWsId, { type: "report", id: selection.reportId });
          } else {
            // Group already in stack — find the last stack position that belongs to this group
            // and insert the new report immediately after it, keeping unrelated items at the end.
            let lastGroupMemberIdx = -1;
            currentStack.forEach((item, idx) => {
              if (groupMemberIds.has(item.id)) lastGroupMemberIdx = idx;
            });
            const afterId = lastGroupMemberIdx !== -1 ? currentStack[lastGroupMemberIdx].id : null;
            if (afterId && lastGroupMemberIdx < currentStack.length - 1) {
              // Items from outside this group sit after — splice the new report in.
              this.props.insertBreadcrumbItemAfter(canonicalWsId, { type: "report", id: selection.reportId }, afterId);
            } else {
              this.props.pushBreadcrumbItem(canonicalWsId, { type: "report", id: selection.reportId });
            }
          }
        } else {
          // Root-level report (no group) — always push to the end.
          this.props.pushBreadcrumbItem(canonicalWsId, { type: "report", id: selection.reportId });
        }
        return;
      }

      // Group or folder navigation.
      const newItemId = selection.groupId || selection.folderPath;
      const newItemType = selection.folderPath ? "folder" : "group";
      // Post-delete / BC-close guard: if the previous URL was a report that is no
      // longer in the stack (it was removed by deleteReport or the BC close button),
      // this folder navigation is system-driven, not user-driven.  Skip the push —
      // the BC already reflects the correct state after the removal.
      if (prevSelection.reportId && !currentStack.some((i) => i.id === prevSelection.reportId)) {
        return;
      }
      if (newItemId && !currentStack.some((item) => item.id === newItemId)) {
        // If there's no tab for this custom app yet (cold page.goto to a group URL),
        // register the tab now so that App Home is rendered in the BC.
        const tabAlreadyExists =
          [...workspaces, ...tabs].some((t) => t?.workspaceId === canonicalWsId || t?._id === canonicalWsId) &&
          tabs.some((t) => t?.workspaceId === canonicalWsId || t?._id === canonicalWsId);
        if (!tabAlreadyExists) {
          const appWsObj = allWsPool.find(
            (w) =>
              (w?.type === "customApplication" || (w?.groupings?.length ?? 0) > 0) &&
              (w._id === canonicalWsId || w.workspaceId === canonicalWsId),
          );
          if (appWsObj) {
            const icon = appWsObj.icon || appWsObj.type;
            this.props.addTabToNav({
              title: appWsObj.name,
              workspaceName: appWsObj.name,
              workspaceId: appWsObj._id || appWsObj.workspaceId,
              children: appWsObj.children,
              activeChildren: [],
              icon,
              type: appWsObj.type,
              groupings: appWsObj.groupings,
              applications: appWsObj.applications,
              order: appWsObj.order,
              alias: appWsObj.alias,
            });
          }
        }
        this.props.pushBreadcrumbItem(canonicalWsId, { type: newItemType, id: newItemId });
      }
      return;
    }

    // ── PowerBI workspace Context Stack ──────────────────────────────────────────
    // Regular PowerBI workspace tabs (tab.type === undefined or "powerBI") get the same
    // context-stack treatment as custom apps.  Excluded: MDM (type "mdh"), user mgmt, etc.
    const activeTabObj = tabs?.[selectedTab?.[0]] || null;
    // On a hard reload the Redux tabs array may still be empty when workspaces arrive.
    // Allow BC population in that case by also matching on the URL itself.
    // This covers both PowerBI report URLs (selection.isPowerBI) and folder/group
    // landing URLs (selection.isLanding + folderPath/groupId) so that reloading
    // while inside a folder correctly re-seeds the breadcrumb stack.
    const isPowerBIWsTab =
      (activeTabObj && (!activeTabObj.type || activeTabObj.type === "powerBI")) ||
      (workspacesJustLoaded &&
        !customAppWsId &&
        (selection.isPowerBI || (selection.isLanding && Boolean(selection.folderPath || selection.groupId))));
    if (isPowerBIWsTab) {
      const appWsId = activeTabObj?.workspaceId || activeTabObj?._id || selection.workspaceId || "";
      if (appWsId) {
        // App Home (bare workspace landing, no folder/report) — keep stack unchanged.
        if (selection.isLanding && !selection.groupId && !selection.folderPath && !selection.reportId) {
          return;
        }

        const wsIndex = getReportingIndex(workspaces);
        const wsStacks = this.props.breadcrumbStacks || {};
        const wsCurrentStack = wsStacks[appWsId] || [];

        // Cold reload / deep-link: if there is no active tab for this workspace yet
        // (tabs array was empty when workspaces loaded), register it now so that
        // App Home is rendered in the breadcrumb bar on the next render cycle.
        if (!activeTabObj) {
          const wsEntry = findWorkspaceByAnyId(appWsId, workspaces);
          if (wsEntry && !tabs.some((t) => t?.workspaceId === appWsId || t?._id === appWsId)) {
            const icon = wsEntry.icon || wsEntry.type;
            this.props.addTabToNav({
              title: wsEntry.name,
              workspaceName: wsEntry.name,
              workspaceId: wsEntry.workspaceId || wsEntry._id,
              children: wsEntry.children || wsEntry.reports || [],
              activeChildren: [],
              icon,
              type: wsEntry.type,
              alias: wsEntry.alias,
            });
          }
        }

        // normWsFolder, resolveWsFolderStackId, getWsParentFolderPath, buildAncestorFolderPaths
        // are imported from ../../lib/breadcrumbNavigation.

        if (selection.reportId) {
          markReportAsSavingIfPending();

          // "new" → real ID transition: the user just saved a newly created report.
          // Remove the "new" placeholder and push the real report ID.
          if (prevSelection.reportId === "new" && selection.reportId !== "new") {
            this.props.removeBreadcrumbItem(appWsId, "new");
            this.props.pushBreadcrumbItem(appWsId, { type: "report", id: selection.reportId });
            this.setState({ savingReportId: selection.reportId });
            return;
          }

          const reportNode = wsIndex?.nodeById?.[selection.reportId]?.node;

          const findReportNodeInTabTree = (nodes, reportId) => {
            if (!Array.isArray(nodes) || !reportId) {
              return null;
            }
            for (const node of nodes) {
              const nodeId = String(node?.id || node?._id || node?.rptId || "");
              const targetId = String(reportId);
              if (nodeId === targetId || (nodeId.includes("+") && nodeId.split("+")[1] === targetId)) {
                return node;
              }
              const nested = findReportNodeInTabTree(node?.activeChildren || node?.children, reportId);
              if (nested) {
                return nested;
              }
            }
            return null;
          };

          const deriveParentFolderPathFromNode = (node) => {
            if (!node) {
              return null;
            }
            const rawPath =
              node?.path || node?.fullPath || (Array.isArray(node?.paths) && node.paths.length > 0 ? node.paths[0] : "");
            if (!rawPath) {
              return null;
            }
            const normalized = String(rawPath).replace(/\/+$/, "");
            if (!normalized) {
              return null;
            }

            const segments = normalized.split("/").filter(Boolean);
            const reportTitle = node?.title || node?.name || "";
            if (reportTitle && segments.length > 0 && segments[segments.length - 1] === reportTitle) {
              segments.pop();
            }
            if (segments.length === 0) {
              return null;
            }
            return segments.join("/");
          };

          const deriveParentFolderPathFromStackContext = () => {
            const stack = Array.isArray(wsCurrentStack) ? wsCurrentStack : [];
            if (stack.length === 0) {
              return null;
            }

            const currentReportId = String(selection.reportId || "");
            let reportIdx = stack.findIndex((item) => item?.type === "report" && String(item?.id || "") === currentReportId);
            if (reportIdx === -1) {
              reportIdx = stack.length;
            }

            for (let idx = reportIdx - 1; idx >= 0; idx -= 1) {
              const stackItem = stack[idx];
              if (stackItem?.type === "folder" && stackItem?.id) {
                return stackItem.id;
              }
            }
            return null;
          };

          const tabTreeReportNode = findReportNodeInTabTree(activeTabObj?.activeChildren, selection.reportId);
          const parentFolderPath =
            getWsParentFolderPath(reportNode, wsIndex) ||
            deriveParentFolderPathFromNode(tabTreeReportNode) ||
            deriveParentFolderPathFromStackContext();

          // Cross-ID dedup: if the stack holds the PBI GUID but the URL was built
          // with the Mongo _id (or vice versa), treat them as the same entry.
          const isReportInWsStack = (reportId) => {
            if (wsCurrentStack.some((i) => i.id === reportId)) return true;
            const node = wsIndex?.nodeById?.[reportId]?.node;
            if (node) {
              const altIds = [node.rptId, node._id, node.id].filter(Boolean);
              if (altIds.some((altId) => wsCurrentStack.some((i) => i.id === altId))) return true;
            }
            return false;
          };
          const reportAlreadyTracked = isReportInWsStack(selection.reportId);
          if (reportAlreadyTracked) {
            const folderPathsForTracked = buildAncestorFolderPaths(parentFolderPath);
            const deepestTrackedPath =
              folderPathsForTracked.length > 0 ? folderPathsForTracked[folderPathsForTracked.length - 1] : "";
            const deepestTrackedExists = Boolean(
              deepestTrackedPath &&
              wsCurrentStack.some((i) => (i.type === "folder" || i.type === "group") && i.id === deepestTrackedPath),
            );

            // Clone/save can initially push a report at root before index/path metadata catches up.
            // When we later resolve a folder parent, re-home the report under that folder chain.
            if (folderPathsForTracked.length > 0 && !deepestTrackedExists) {
              this.props.removeBreadcrumbItem(appWsId, selection.reportId);
              folderPathsForTracked.forEach((fp) => {
                if (!wsCurrentStack.some((i) => i.id === fp)) {
                  this.props.pushBreadcrumbItem(appWsId, { type: "folder", id: fp });
                }
              });
              this.props.pushBreadcrumbItem(appWsId, { type: "report", id: selection.reportId });
            }
            return;
          }

          const folderPaths = buildAncestorFolderPaths(parentFolderPath);

          if (folderPaths.length > 0) {
            const deepestPath = folderPaths[folderPaths.length - 1]; // = parentFolderPath
            const folderEntry = wsCurrentStack.find((i) => (i.type === "folder" || i.type === "group") && i.id === deepestPath);
            if (!folderEntry) {
              // Deepest parent folder not yet in stack — push all missing ancestor folders
              // (outermost → innermost) then the report.
              for (const fp of folderPaths) {
                if (!wsCurrentStack.some((i) => i.id === fp)) {
                  this.props.pushBreadcrumbItem(appWsId, { type: "folder", id: fp });
                }
              }
              this.props.pushBreadcrumbItem(appWsId, { type: "report", id: selection.reportId });
            } else {
              // Deepest parent already in stack — splice the new report after the last
              // member of that folder (same logic as custom-app groups).
              const folderMemberIds = new Set([folderEntry.id]);
              wsCurrentStack
                .filter((i) => i.type === "report")
                .forEach((i) => {
                  const rn = wsIndex?.nodeById?.[i.id]?.node;
                  if (getWsParentFolderPath(rn, wsIndex) === deepestPath) folderMemberIds.add(i.id);
                });

              let lastMemberIdx = -1;
              wsCurrentStack.forEach((item, idx) => {
                if (folderMemberIds.has(item.id)) lastMemberIdx = idx;
              });

              const afterId = lastMemberIdx !== -1 ? wsCurrentStack[lastMemberIdx].id : null;
              if (afterId && lastMemberIdx < wsCurrentStack.length - 1) {
                this.props.insertBreadcrumbItemAfter(appWsId, { type: "report", id: selection.reportId }, afterId);
              } else {
                this.props.pushBreadcrumbItem(appWsId, { type: "report", id: selection.reportId });
              }
            }
          } else {
            // Root-level (unfiled) report — no folder ancestors.
            this.props.pushBreadcrumbItem(appWsId, { type: "report", id: selection.reportId });
          }
          return;
        }

        // Folder / group navigation.
        const rawSegment = selection.folderPath || selection.groupId;
        if (rawSegment) {
          // Post-delete / BC-close guard: if the previous URL was a report that is no
          // longer in the stack, this folder navigation is system-driven.  Skip the
          // push — the BC already reflects the correct state after removal.
          if (prevSelection.reportId && !wsCurrentStack.some((i) => i.id === prevSelection.reportId)) {
            return;
          }
          const canonicalId = resolveWsFolderStackId(rawSegment, wsIndex);
          if (canonicalId && !wsCurrentStack.some((i) => i.id === canonicalId)) {
            // Push all ancestor folders (outermost → innermost) so that a cold reload
            // or deep-link into a subfolder rebuilds the full BC hierarchy without
            // requiring the user to have visited each parent folder individually.
            const ancestorPaths = buildAncestorFolderPaths(canonicalId);
            const pathsToPush = ancestorPaths.length > 0 ? ancestorPaths : [canonicalId];
            for (const fp of pathsToPush) {
              if (!wsCurrentStack.some((i) => i.id === fp)) {
                this.props.pushBreadcrumbItem(appWsId, { type: "folder", id: fp });
              }
            }
          }
        }
        return;
      }
    }

    // ── Non-custom-app legacy breadcrumb storage (MDM and other non-PBI types) ──
    if (selection?.reportId) {
      if (prevSelection?.workspaceId && prevSelection.workspaceId !== selection.workspaceId) {
        this.props.setBreadcrumbSelection(prevSelection.workspaceId, {
          workspaceId: prevSelection.workspaceId,
          groupId: prevSelection.groupId || "",
          folderPath: prevSelection.folderPath || "",
          reportId: selection.reportId,
        });
      }
      if (selection.workspaceId) {
        this.props.setBreadcrumbSelection(selection.workspaceId, selection);
      }
      return;
    }

    if (selection?.workspaceId && (selection.groupId || selection.folderPath)) {
      this.props.setBreadcrumbSelection(selection.workspaceId, selection);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside(event) {
    if (this.navRef.current && !this.navRef.current.contains(event.target)) {
      this.setState({ showClose: -1 });
    }
  }

  goTo(route) {
    if (this.props.location.pathname !== `/${route}`) {
      this.props.navigate(`/${route}`, { replace: true });
    }
  }

  async clickHandler(clicked, index, type, createNew = true) {
    const isGuidLike = (value) => typeof value === "string" && (value.length === 32 || value.length === 36);
    const isMongoId = (value) => typeof value === "string" && value.length === 24;
    const extractWorkspaceIdFromUrl = (url) => {
      if (typeof url !== "string") {
        return "";
      }
      const match = url.match(/groups\/([a-f0-9-]{36})/i);
      return match?.[1] || "";
    };
    const isGuid = (value) => typeof value === "string" && value.length === 36;
    const resolveWorkspaceIdFromReport = (report) => {
      if (!report) {
        return "";
      }
      if (isGuid(report.workspaceId)) {
        return report.workspaceId;
      }
      return extractWorkspaceIdFromUrl(report.webUrl) || extractWorkspaceIdFromUrl(report.embedUrl) || "";
    };
    const findReportInNodes = (nodes, reportKey) => {
      if (!reportKey || !Array.isArray(nodes)) {
        return null;
      }
      for (const node of nodes) {
        if (!node) {
          continue;
        }
        if (node.id === reportKey || node._id === reportKey || node.rptId === reportKey) {
          return node;
        }
        const nestedMatch = findReportInNodes(node.children, reportKey) || findReportInNodes(node.activeChildren, reportKey);
        if (nestedMatch) {
          return nestedMatch;
        }
      }
      return null;
    };
    const resolveWorkspaceIdFromTab = (tab, reportKey) => {
      if (!tab || !reportKey) {
        return "";
      }
      const pools = [];
      if (Array.isArray(tab.groupings)) {
        pools.push(...tab.groupings);
      }
      if (Array.isArray(tab.fileGroupings)) {
        pools.push(...tab.fileGroupings);
      }
      if (Array.isArray(tab.children)) {
        pools.push(...tab.children);
      }
      if (Array.isArray(tab.activeChildren)) {
        pools.push(...tab.activeChildren);
      }
      const match = findReportInNodes(pools, reportKey);
      return resolveWorkspaceIdFromReport(match);
    };
    const resolveReportFromId = (reportKey) => {
      if (!reportKey || !Array.isArray(this.props.workspaces)) {
        return null;
      }
      for (const ws of this.props.workspaces) {
        if (!Array.isArray(ws.reports)) {
          continue;
        }
        const match = ws.reports.find((rpt) => rpt.id === reportKey || rpt._id === reportKey || rpt.rptId === reportKey);
        if (match) {
          return { report: match, workspaceId: ws.workspaceId };
        }
      }
      for (const ws of this.props.workspaces) {
        const childMatch = findReportInNodes(ws.children, reportKey);
        if (childMatch) {
          const extractedWorkspaceId = resolveWorkspaceIdFromReport(childMatch);
          return { report: childMatch, workspaceId: extractedWorkspaceId || ws.workspaceId };
        }
      }
      for (const ws of this.props.workspaces) {
        const groupingPools = [];
        if (Array.isArray(ws.groupings)) {
          groupingPools.push(...ws.groupings);
        }
        if (Array.isArray(ws.fileGroupings)) {
          groupingPools.push(...ws.fileGroupings);
        }
        if (groupingPools.length === 0) {
          continue;
        }
        for (const grp of groupingPools) {
          if (!Array.isArray(grp.children)) {
            continue;
          }
          const match = grp.children.find(
            (child) => child.id === reportKey || child._id === reportKey || child.rptId === reportKey,
          );
          if (match) {
            const extractedWorkspaceId =
              (typeof match.workspaceId === "string" && match.workspaceId.length === 36 && match.workspaceId) ||
              extractWorkspaceIdFromUrl(match.webUrl) ||
              extractWorkspaceIdFromUrl(match.embedUrl) ||
              "";
            return { report: match, workspaceId: extractedWorkspaceId || ws.workspaceId };
          }
        }
      }
      return null;
    };
    const fallbackReport = resolveReportFromId(clicked?.id);
    // Derive the best PBI GUID for the clicked report. ws.reports entries sometimes
    // lack rptId for deeply-nested subfolder reports, so also walk ws.children which
    // carries the full node data from the PowerBI folder tree.
    const fallbackRptId = (() => {
      if (fallbackReport?.report?.rptId) return fallbackReport.report.rptId;
      const searchKey = clicked?._id || clicked?.id;
      if (!searchKey || !Array.isArray(this.props.workspaces)) return null;
      for (const ws of this.props.workspaces) {
        const node = findReportInNodes(ws.children, searchKey);
        if (node?.rptId) return node.rptId;
      }
      return null;
    })();
    const resolveReportIdFromIndex = () => {
      if (!Array.isArray(index) || index.length < 2) {
        return "";
      }
      const tab = this.props.tabs?.[index[0]];
      if (!tab) {
        return "";
      }
      if (index[3] !== undefined && index[3] !== -1) {
        const reportItem = tab.activeChildren?.[index[1]]?.activeChildren?.[index[2]]?.activeChildren?.[index[3]];
        return reportItem?.rptId || reportItem?._id || reportItem?.id || "";
      }
      if (index[2] !== undefined && index[2] !== -1) {
        const reportItem = tab.activeChildren?.[index[1]]?.activeChildren?.[index[2]];
        return reportItem?.rptId || reportItem?._id || reportItem?.id || "";
      }
      const reportItem = tab.activeChildren?.[index[1]];
      return reportItem?.rptId || reportItem?._id || reportItem?.id || "";
    };
    let reportIdPreferred =
      clicked?.rptId ||
      // Prefer PBI GUID over Mongo _id so the URL always matches the ID already
      // stored in the breadcrumb stack (chips are pushed with the PBI GUID when a
      // report is first opened via tiles). fallbackRptId also searches ws.children
      // so subfolder reports whose ws.reports entry lacks rptId are still resolved.
      fallbackRptId ||
      (isMongoId(clicked?._id) ? clicked._id : undefined) ||
      (isMongoId(clicked?.id) ? clicked.id : undefined) ||
      fallbackReport?.report?._id ||
      clicked?.id;
    if ((type === "fileGroupings" || type === "powerBI") && !clicked?.rptId) {
      const reportIdFromIndex = resolveReportIdFromIndex();
      if (reportIdFromIndex && reportIdPreferred === clicked?.id) {
        reportIdPreferred = reportIdFromIndex;
      }
    }
    const resolvedWorkspaceIdFromTab = resolveWorkspaceIdFromTab(
      clicked?.tab || this.props.tabs?.[index?.[0]],
      reportIdPreferred,
    );
    let workspaceIdOverride =
      (clicked?.rptId && clicked.id !== reportIdPreferred && isGuidLike(clicked?.id) ? clicked.id : "") ||
      resolvedWorkspaceIdFromTab ||
      fallbackReport?.workspaceId ||
      "";
    if (type === "fileGroupings" && Array.isArray(index) && index.length >= 3) {
      const parentGroup = this.props.tabs?.[index[0]]?.activeChildren?.[index[1]];
      if (parentGroup?.id) {
        workspaceIdOverride = parentGroup.id;
      }
    }
    const resolveWorkspaceIdForReport = (reportKey) => {
      if (!reportKey || !Array.isArray(this.props.workspaces)) {
        return "";
      }
      const directMatch = this.props.workspaces.find((ws) => {
        if (Array.isArray(ws.reports)) {
          return ws.reports.some((rpt) => rpt.id === reportKey || rpt._id === reportKey || rpt.rptId === reportKey);
        }
        return false;
      });
      if (directMatch?.workspaceId) {
        return directMatch.workspaceId;
      }
      const childMatch = this.props.workspaces.find((ws) => findReportInNodes(ws.children, reportKey));
      if (childMatch) {
        const match = findReportInNodes(childMatch.children, reportKey);
        const extractedWorkspaceId = resolveWorkspaceIdFromReport(match);
        if (extractedWorkspaceId) {
          return extractedWorkspaceId;
        }
        return childMatch.workspaceId || "";
      }
      const groupingMatch = this.props.workspaces.find((ws) => {
        const groupingPools = [];
        if (Array.isArray(ws.groupings)) {
          groupingPools.push(...ws.groupings);
        }
        if (Array.isArray(ws.fileGroupings)) {
          groupingPools.push(...ws.fileGroupings);
        }
        if (groupingPools.length === 0) {
          return false;
        }
        return groupingPools.some((grp) => {
          if (!Array.isArray(grp.children)) {
            return false;
          }
          return grp.children.some((child) => child.rptId === reportKey || child.id === reportKey || child._id === reportKey);
        });
      });
      if (!groupingMatch) {
        return "";
      }
      const groupingPools = [];
      if (Array.isArray(groupingMatch.groupings)) {
        groupingPools.push(...groupingMatch.groupings);
      }
      if (Array.isArray(groupingMatch.fileGroupings)) {
        groupingPools.push(...groupingMatch.fileGroupings);
      }
      for (const grp of groupingPools) {
        if (!Array.isArray(grp.children)) {
          continue;
        }
        const match = grp.children.find(
          (child) => child.rptId === reportKey || child.id === reportKey || child._id === reportKey,
        );
        if (match) {
          const extractedWorkspaceId =
            (typeof match.workspaceId === "string" && match.workspaceId.length === 36 && match.workspaceId) ||
            extractWorkspaceIdFromUrl(match.webUrl) ||
            extractWorkspaceIdFromUrl(match.embedUrl) ||
            "";
          return extractedWorkspaceId || groupingMatch.workspaceId || "";
        }
      }
      return groupingMatch.workspaceId || "";
    };
    const resolveWorkspaceId = (reportKey = reportIdPreferred) => {
      const reportWorkspaceId = resolveWorkspaceIdForReport(reportKey);
      if (reportWorkspaceId) {
        return reportWorkspaceId;
      }
      if (resolvedWorkspaceIdFromTab) {
        return resolvedWorkspaceIdFromTab;
      }
      if (clicked?.workspaceId && isGuid(clicked.workspaceId)) {
        return clicked.workspaceId;
      }
      if (clicked?.tab?.workspaceId && isGuid(clicked.tab.workspaceId)) {
        return clicked.tab.workspaceId;
      }
      if (clicked?.workspace?.workspaceId) {
        return clicked.workspace.workspaceId;
      }
      const tabWorkspaceId = this.props.tabs?.[index?.[0]]?.workspaceId;
      if (tabWorkspaceId) {
        return tabWorkspaceId;
      }
      return this.props.reportGroup || "";
    };
    let updatedIdx = false;

    if (this.props.tabs[index[0]]?.activeChildren !== undefined) {
      if (index !== undefined && index[2] === undefined) {
        this.props.tabs[index[0]].activeChildren.forEach((item, xIdx) => {
          if (item.activeChildren !== undefined && item.activeChildren.length > 0) {
            item.activeChildren.forEach((iChild, idx) => {
              if (iChild.id === clicked.id) {
                index[1] = xIdx;
                updatedIdx = true;
              }
            });
          } else if (item.id === clicked.id) {
            index[1] = xIdx;
            updatedIdx = true;
          }
        });
        if (!updatedIdx) {
          index[1] = this.props.tabs[index[0]].activeChildren.length - 1;
          updatedIdx = true;
        }
      } else if (index !== undefined && index[3] === undefined) {
        this.props.tabs[index[0]].activeChildren.forEach((item, xIdx) => {
          if (item.activeChildren !== undefined) {
            item.activeChildren.forEach((gItem, gIdx) => {
              if (gItem.id === clicked.id) {
                index[1] = xIdx;
                index[2] = gIdx;
                updatedIdx = true;
              }
            });
          }
        });
      }
      if (!updatedIdx) {
        if (index.length === 2 && this.props.tabs[index[0]]?.activeChildren) {
          index[1] = this.props.tabs[index[0]].activeChildren.length - 1;
        }
      }
    }

    switch (type) {
      case "powerBI":
      case "fileGroupings":
        let modifiedClicked = { ...clicked };
        delete modifiedClicked.clickHandler;
        delete modifiedClicked.removeHandler;
        let id;
        const reportId = reportIdPreferred;
        if (!modifiedClicked.rptId) {
          const resolvedReportId =
            fallbackReport?.report?.rptId || fallbackReport?.report?.id || fallbackReport?.report?._id || reportId;
          if (resolvedReportId) {
            modifiedClicked.rptId = resolvedReportId;
          }
        }
        if (reportId !== undefined) {
          if (clicked.tab === undefined) {
            //check for proper id when coming from a subfolder this can be different
            if (reportId.includes("+")) {
              id = reportId;
            } else {
              if (clicked.workspaceId === undefined) {
                const resolvedWorkspaceId = resolveWorkspaceId(reportId);
                clicked.tab = {
                  workspace: clicked.workspace,
                  workspaceId: workspaceIdOverride || resolvedWorkspaceId,
                };
                let ctWorkspace = clicked.tab.workspaceId;
                if (ctWorkspace === undefined && clicked.tab.workspace !== undefined) {
                  let findaWS = this.props.workspaces.find((item) => {
                    return clicked.tab.workspace === item._id;
                  });
                  ctWorkspace = findaWS?.workspaceId;
                }
                if (ctWorkspace === undefined) {
                  ctWorkspace = this.props.reportGroup || resolvedWorkspaceId;
                }
                if (isMongoId(ctWorkspace)) {
                  const resolvedFromReport = resolveWorkspaceIdForReport(reportId);
                  if (resolvedFromReport) {
                    ctWorkspace = resolvedFromReport;
                  }
                }
                id = ctWorkspace + "+" + reportId;
                //id = clicked.workspace.workspaceId + "+" + clicked.id;
              } else {
                clicked.tab = {
                  workspace: clicked.workspace,
                  workspaceId: clicked.workspaceId,
                };
                let ctWorkspace = workspaceIdOverride || clicked.workspaceId;
                if (isMongoId(ctWorkspace)) {
                  const resolvedFromReport = resolveWorkspaceIdForReport(reportId);
                  if (resolvedFromReport) {
                    ctWorkspace = resolvedFromReport;
                  }
                }
                id = ctWorkspace + "+" + reportId;
              }
            }
          } else {
            if (reportId.includes("+")) {
              id = reportId;
            } else {
              let ctWorkspace = clicked.tab.workspaceId;
              if (ctWorkspace === undefined) {
                const resolvedWorkspaceId = resolveWorkspaceId(reportId);
                if (clicked.tab.workspace !== undefined) {
                  let findaWS = this.props.workspaces.find((item) => {
                    return clicked.tab.workspace === item._id;
                  });
                  ctWorkspace = findaWS?.workspaceId;
                }
                if (ctWorkspace === undefined) {
                  ctWorkspace = this.props.reportGroup || resolvedWorkspaceId;
                }
              }
              if (workspaceIdOverride) {
                ctWorkspace = workspaceIdOverride;
              }
              if (isMongoId(ctWorkspace)) {
                const resolvedFromReport = resolveWorkspaceIdForReport(reportId);
                if (resolvedFromReport) {
                  ctWorkspace = resolvedFromReport;
                }
              }
              id = ctWorkspace + "+" + reportId;
            }
          }

          modifiedClicked.combiId = id;
          //there are a handful of ways this is called which use different names for the "title"
          modifiedClicked.title = modifiedClicked.text;
          modifiedClicked.selected = true;
          if (modifiedClicked.title === undefined || modifiedClicked.title === null || modifiedClicked.title === "") {
            modifiedClicked.title = modifiedClicked.name;
          }

          const ownerTab = this.props.tabs[index[0]];
          const ownerWorkspace = this.props.workspaces.find(
            (workspace) => workspace._id === (ownerTab?._id || ownerTab?.workspaceId),
          );
          const ownerApp = this.isCustomApp(ownerWorkspace)
            ? ownerWorkspace
            : this.isCustomApp(ownerTab) ? ownerTab : null;
          const ownerAppId = ownerApp?._id || ownerApp?.workspaceId;
          this.props.setReportGroup(ownerAppId || id.split("+")[0]);

          this.props.setSelectedTab(index);
          this.props.navigate("/powerBI/report/" + id + (ownerAppId ? "?appId=" + encodeURIComponent(ownerAppId) : ""));
        } else {
          //if the id is null this is a (abstract) powerbi folder we are moving over to
          // QA&+Test%20Folder%201&+Subfolder%201&+
          this.props.navigate(
            "/reports/landing/" + this.props.reportGroup + "/" + encodeURI(clicked.path).replace(/\//g, "&+"),
          ); //add path

          this.props.setSelectedTab(index);
        }
        this.props.setSelectedTab(index);
        break;
      case "files":
      case "Files":
        this.props.setSelectedTab(index);
        this.props.navigate("/files/landing");
        break;
      case "tableau":
      case "tableauGroupings":
        let tModifiedClicked = { ...clicked };
        delete tModifiedClicked.clickHandler;
        delete tModifiedClicked.removeHandler;
        let tId;
        if (clicked.tab === undefined) {
          //check for proper id when coming from a subfolder this can be different
          if (clicked.id.includes("+")) {
            tId = clicked.id;
          } else {
            clicked.tab = {
              workspace: clicked.workspace,
              workspaceId: clicked.workspaceId,
            };
            if (clicked.tab === undefined || clicked.tab.workspaceId === undefined) {
              this.props.workspaces.forEach((item) => {
                if (item._id === clicked.workspace) {
                  tId = item.workspaceId + "+" + clicked.id;
                }
              });
            } else {
              tId = clicked.workspaceId + "+" + clicked.id;
            }
          }
        } else {
          if (clicked.id.includes("+")) {
            tId = clicked.id;
          } else {
            if (clicked.tab === undefined || clicked.tab.workspaceId === undefined) {
              this.props.workspaces.forEach((item) => {
                if (item._id === clicked.workspace) {
                  tId = item.workspaceId + "+" + clicked.id;
                }
              });
            } else {
              let ctWorkspace = clicked.tab.workspaceId;
              if (ctWorkspace === undefined) {
                let findaWS = this.props.workspaces.find((item) => {
                  return clicked.tab.workspace === item._id;
                });
                ctWorkspace = findaWS.workspaceId;
              }
              tId = ctWorkspace + "+" + clicked.id;
            }
          }
        }

        tModifiedClicked.id = tId;
        //there are a handful of ways this is called which use different names for the "title"
        tModifiedClicked.title = tModifiedClicked.text;

        if (tModifiedClicked.title === undefined || tModifiedClicked.title === null || tModifiedClicked.title === "") {
          tModifiedClicked.title = tModifiedClicked.name;
        }
        this.props.navigate("/tableau/report/" + tId);
        this.props.setSelectedTab(index);
        break;
      // this.props.navigate("/tableau/report/" + clicked.id);
      // break;
      case "staticContent":
        this.props.setSelectedTab(index);
        if (clicked.tab === undefined) {
          this.props.navigate("/sc/report/" + clicked.workspaceId + "+" + clicked.id);
        } else {
          this.props.navigate("/sc/report/" + clicked.tab.workspaceId + "+" + clicked.id);
        }
        break;
      case "mdh":
        this.props.setSelectedTab(index);
        this.props.navigate("/mdm/dataManagement/" + clicked.id);
        break;
      case "insightsDiscovery":
        this.props.setSelectedTab(index);
        this.props.navigate("/insightsDiscovery/runs");
        break;
      case "userManagement":
        this.props.setSelectedTab(index);
        this.props.navigate("/userManagement");
        break;
      case "reportSubscriptions":
        this.props.setSelectedTab(index);
        this.props.navigate("/reportSubscriptions");
        break;
      case "powerBIMgmt":
        this.props.setSelectedTab(index);
        this.props.navigate("/powerBIMgmt");
        break;
      case "grouping":
        this.props.setSelectedTab(index[0], index[1], 0);
        this.props.setCustomGroup(clicked.id);
        this.props.navigate("/reports/landing/" + this.props.reportGroup + "/" + clicked.id);
        break;
      case "tab":
        this.props.setSelectedTab(index);
        this.props.setReportGroup(clicked.workspaceId);
        this.props.setCustomGroup("");
        this.props.navigate("/reports/landing/" + clicked.workspaceId);

        break;
      case "folder":
        let rg = clicked.workspaceId;
        if (clicked.workspaceId === undefined) {
          let findThis = clicked.path.split("/")[0];
          const ws = this.props.workspaces.find((item) => {
            return item.name === findThis;
          });
          this.props.setReportGroup(ws.workspaceId);
          rg = ws.workspaceId;
        } else {
          this.props.setReportGroup(clicked.workspaceId);
        }
        this.props.setSelectedTab(index);
        this.props.navigate("/reports/landing/" + rg + "/" + encodeURI(clicked.path).replace(/\//g, "&+"));

        this.props.setCustomGroup(clicked.path.replace(/\//g, "&+"));

        break;
      // this.props.setCustomGroup("");
      default:
        this.props.setSelectedTab(index);
        this.props.navigate("/reports/landing/" + this.props.reportGroup + "/" + encodeURI(clicked.path).replace(/\//g, "&+"));
        if (type === undefined) {
          this.props.setCustomGroup(clicked.path.replace(/\//g, "&+"));
        }

        break;
    }
    if (index !== undefined && index[1] === undefined) {
      let idx = 0;
      for (let item of this.props.tabs[index[0]].activeChildren) {
        if (item.workspaceId === clicked.workspaceId) {
          index[1] = idx;
        }
        idx = idx + 1;
      }
    }
    if (!createNew) {
      this.props.setSelectedTab(index);
    }
  }
  homeClickHandler() {
    const { defaultApp, homePage } = this.props;
    this.props.setReportGroup("");
    this.props.setCustomGroup("");
    this.props.setSelectedTab([-1, -1]);
    var defaultFound;

    if (homePage !== undefined && homePage !== null && Object.entries(homePage).length > 0 && homePage.link) {
      if (homePage.link.includes("report")) {
        this.props.navigate("/reports/landing");
        this.props.setCustomGroup("");
        this.props.setReportGroup("");
      } else {
        this.props.navigate(homePage.link);
      }
    } else if (defaultApp == null) {
    } else if (defaultApp.link !== undefined) {
      if (defaultApp.link.includes("/landing") && this.props.workspaces.length > 0 && defaultApp.link.split("/").length > 3) {
        defaultFound = this.props.workspaces.filter((item) => {
          return defaultApp.name.toLowerCase() === item.name.toLowerCase();
        });
      } else {
        defaultFound = this.props.apps.filter((item) => {
          if (item.link === undefined) {
            return defaultApp.name.toLowerCase() === item.type.toLowerCase();
          } else {
            return defaultApp.link.includes(item.link);
          }
        });
      }
      if (defaultFound.length > 0) {
        if (defaultApp.link.includes("report")) {
          this.props.navigate("/reports/landing");
          this.props.setCustomGroup("");
          this.props.setReportGroup("");
        } else if (defaultApp.link.includes("dataManagement")) {
          let navItem = defaultApp.link;
          let dmArrGroups = this.props.apps.filter((item) => {
            return item.type === "mdh";
          });
          dmArrGroups = dmArrGroups[0];
          let navItemBase = navItem.split("dataManagement/")[0];

          this.props.navigate(navItemBase + "dataManagement/" + dmArrGroups.groups[0].ID + "+0+0", { replace: true });
        } else {
          this.props.navigate(defaultApp.link, { replace: true });
        }
      } else if (this.props.apps.length > 0) {
        if (this.props.apps[0].type === "powerBI" || this.props.apps[0].type === "tableau") {
          this.props.navigate("/reports", { replace: true });
        } else {
          this.props.navigate(this.props.apps[0].link, { replace: true });
        }
      } else {
        this.props.navigate("/noPermissions", { replace: true });
      }
    } else {
      let rptsFound = this.props.apps.filter((item) => {
        if (item.link !== undefined) {
          return item.link.includes("/reports");
        } else {
          return item.type === "reporting";
        }
      });
      if (rptsFound.length > 0) {
        this.props.navigate("/reports", { replace: true });
      } else if (this.props.apps.length > 0) {
        if (this.props.apps[0].type === "powerBI" || this.props.apps[0].type === "tableau") {
          this.props.navigate("/reports", { replace: true });
        } else {
          this.props.navigate(this.props.apps[0].link, { replace: true });
        }
      } else {
        this.props.navigate("/noPermissions", { replace: true });
      }
    }
  }
  renderTabs(routeSelection = {}) {
    const { tabs, selectedTab, searchTabs } = this.props;

    if (tabs.length !== 0) {
      let baseApps = this.props.apps.filter((item) => {
        if (item.inheritBaseApp === null || item.inheritBaseApp === undefined) {
          return false;
        }
        return item.inheritBaseApp;
      });
      let baseAppsArr = cloneDeep(baseApps);
      baseAppsArr = baseAppsArr.map((item) => {
        return item.type;
      });

      return searchTabs.map((tab, index) => {
        // if (tab.workspaceId !== undefined) {
        if (baseAppsArr.includes(tab.type)) {
          baseApps.forEach((item) => {
            if (item.type === tab.type) {
              tab.icon = item.icon;
            }
          });
        }
        if (tab.children === undefined) {
          if (tab.type === "powerBI" || tab.type === "tableau" || tab.type === "customApplication") {
            let nTab = this.props.workspaces.filter((item) => {
              if (item.workspaceId === undefined) {
                return item._id === this.props.activeWS;
              } else {
                return item.workspaceId === this.props.activeWS;
              }
            });
            if (nTab.length > 0) {
              tab.children = nTab[0].reports;
            }
          } else {
            return (
              <TopLevelParentNavItem
                tab={tab}
                tabs={tabs}
                key={index}
                index={index}
                activeWS={this.props.activeWS}
                workspaces={this.props.workspaces}
                selectedTab={this.props.selectedTab}
                parentSelected={selectedTab[0] === index}
                clickHandler={this.clickHandler}
                removeHandler={(e, item) => this.closeMenuReport(tab.workspaceId || tab._id, item)}
                breadcrumbStacks={this.props.breadcrumbStacks}
                apps={this.props.apps}
                filterTab={this.props.filterTab}
                onClose={this.handleTabClose}
              />
            );
          }
        }

        return (
          <TopLevelParentNavItem
            tab={tab}
            tabs={tabs}
            key={index}
            index={index}
            activeWS={this.props.activeWS}
            workspaces={this.props.workspaces}
            selectedTab={this.props.selectedTab}
            parentSelected={selectedTab[0] === index}
            clickHandler={this.clickHandler}
            removeHandler={(e, item) => this.closeMenuReport(tab.workspaceId || tab._id, item)}
            breadcrumbStacks={this.props.breadcrumbStacks}
            apps={this.props.apps}
            filterTab={this.props.filterTab}
            routeSelection={routeSelection}
            onClose={this.handleTabClose}
          />
        );
      });
    } else return;
  }
  handleTabClose(tab) {
    const { tabs, selectedTab, defaultApp } = this.props;
    const closingIndex = tabs.findIndex((t) => {
      if (tab.workspaceId) return t.workspaceId === tab.workspaceId;
      if (tab.id) return t.id === tab.id;
      return t.type === tab.type;
    });
    const closingWorkspaceId = tab.workspaceId || tab._id || "";
    if (closingWorkspaceId) {
      this.props.clearBreadcrumbSelection(closingWorkspaceId);
    }
    if (tab.type === "files") {
      // Unlike navigating to Home/another app (where the filter is meant to
      // persist — see FileList's searchTerm), actually closing the Files tab
      // means reopening it later should start fresh rather than silently
      // still be filtered.
      this.props.clearFileSearch();
    }
    this.props.removeTabFromNav(tab, 1);
    this.props.setCustomGroup("");

    if (closingIndex === -1) return;

    // Determine if the closing tab is the currently active one by comparing identity,
    // not index. selectedTab[0] can be stale (e.g. a previous non-active close removed
    // a tab at a lower index, sliding this tab down without updating selectedTab).
    const selectedTabData = selectedTab[0] >= 0 && selectedTab[0] < tabs.length ? tabs[selectedTab[0]] : null;
    const isActiveTab = selectedTabData
      ? tab.workspaceId
        ? tab.workspaceId === selectedTabData.workspaceId
        : tab.id
          ? tab.id === selectedTabData.id
          : tab.type === selectedTabData.type
      : true; // selectedTab is out-of-bounds/stale — always navigate to avoid orphaned view

    if (!isActiveTab) {
      // If we closed a tab at a lower index than the active one, the active tab
      // has shifted down by one — update selectedTab to keep it highlighted.
      if (closingIndex < selectedTab[0]) {
        this.props.setSelectedTab([selectedTab[0] - 1, selectedTab[1]]);
      }
      return;
    }

    let workspaceId;
    if (tabs.length === 1) {
      this.props.setSelectedTab([-1, -1]);
      this.props.setReportGroup("");
      this.props.navigate(defaultApp?.link || "/");
    } else if (closingIndex > 0) {
      const prevTab = tabs[closingIndex - 1];
      if (prevTab.workspaceId === undefined) {
        this.props.setReportGroup("");
        this.props.navigate(prevTab.path);
      } else {
        workspaceId = prevTab.workspaceId;
        this.props.setReportGroup(workspaceId);
        this.props.navigate("/reports/landing/" + workspaceId);
      }
      this.props.setSelectedTab([closingIndex - 1, -1]);
    } else {
      const nextTab = tabs[closingIndex + 1];
      if (nextTab.workspaceId === undefined) {
        this.props.setReportGroup("");
        this.props.navigate(nextTab.path);
      } else {
        workspaceId = nextTab.workspaceId;
        this.props.setReportGroup(workspaceId);
        this.props.navigate("/reports/landing/" + workspaceId);
      }
      this.props.setSelectedTab([0, -1]);
    }
  }
  // Closes a report/folder/grouping opened from the workspace or custom-app dropdown
  // menu using the exact same "sweep descendants, then shift left to the nearest
  // surviving crumb" behavior as the breadcrumb bar's own close (see the `onClose`
  // closure inside renderBreadcrumbStack) — driven off the same breadcrumbStacks
  // state rather than the legacy tab.activeChildren tree childRemoveHandler uses.
  closeMenuReport(wsId, node) {
    if (!wsId) {
      return;
    }
    const stack = (this.props.breadcrumbStacks || {})[wsId] || [];
    if (stack.length === 0) {
      return;
    }

    const isFolderNode = node.fileType === "folder";
    const isGroupingNode = node.type === "grouping";
    const normalizedPath = node.path ? node.path.replace(/\/+$/, "") : "";
    const idCandidates = [node.id, node._id, node.rptId, node.reportTestId].filter(Boolean);

    const stackItem = stack.find((s) => {
      if (isFolderNode) {
        return s.type === "folder" && (s.id === normalizedPath || idCandidates.includes(s.id));
      }
      if (isGroupingNode) {
        return s.type === "group" && idCandidates.includes(s.id);
      }
      return s.type === "report" && idCandidates.includes(s.id);
    });
    if (!stackItem) {
      // Not actually tracked as open in the breadcrumb stack — nothing to close.
      return;
    }

    const { workspaces } = this.props;
    const index = workspaces?.length ? getReportingIndex(workspaces) : null;

    const getImmediateParentId = (reportId) => {
      const ws = workspaces?.find((w) => (w._id || w.workspaceId) === wsId);
      if (ws?.groupings) {
        for (const g of ws.groupings) {
          const kids = g.children || [];
          if (kids.some((c) => c.rptId === reportId || c.id === reportId || c._id === reportId)) {
            return g.id || g._id || null;
          }
        }
      }
      const reportNode = index?.nodeById?.[reportId]?.node;
      if (reportNode) {
        const reportKey = reportNode.id || reportNode._id || reportNode.rptId || "";
        const rawPathIds = reportNode.pathIds ? String(reportNode.pathIds) : "";
        const pathIds = rawPathIds
          .split("/")
          .filter(Boolean)
          .filter((id) => id !== reportKey);
        if (pathIds.length > 0) {
          const deepestFolderId = pathIds[pathIds.length - 1];
          const folderNode = index?.nodeById?.[deepestFolderId]?.node;
          if (folderNode?.path) return folderNode.path.replace(/\/+$/, "");
        }
        const rawPath = reportNode.path || reportNode.fullPath || "";
        if (rawPath) {
          const segments = rawPath.replace(/\/+$/, "").split("/").filter(Boolean);
          const rptTitle = reportNode.title || reportNode.name || "";
          if (rptTitle && segments[segments.length - 1] === rptTitle) segments.pop();
          if (segments.length > 0) return segments.join("/");
        }
      }
      return null;
    };

    const routeForStackItem = (item) => {
      if (item.type === "report") {
        const entry = index?.nodeById?.[item.id];
        const reportNode = entry?.node;
        if (reportNode?.type === "staticContent") {
          const scWs = entry?.workspace;
          const scWsId = scWs?.workspaceId || scWs?._id || wsId;
          return `/sc/report/${scWsId}+${item.id}`;
        }
        const isPbiGuid = (v) => typeof v === "string" && v.length === 36;
        const pbiWsId =
          (isPbiGuid(entry?.workspace?.workspaceId) ? entry.workspace.workspaceId : null) ||
          (isPbiGuid(reportNode?.workspaceId) ? reportNode.workspaceId : null) ||
          (isPbiGuid(wsId) ? wsId : null) ||
          entry?.workspace?.workspaceId ||
          entry?.workspace?._id ||
          wsId;
        return `/powerBI/report/${pbiWsId}+${item.id}`;
      }
      if (item.type === "folder") {
        const encoded = item.id
          .split("/")
          .filter(Boolean)
          .map((s) => encodeURIComponent(s))
          .join("&+");
        return `/reports/landing/${wsId}/${encoded}&+`;
      }
      return `/reports/landing/${wsId}/${item.id}`;
    };

    const isContainer = stackItem.type === "folder" || stackItem.type === "group";
    const idsToRemove = new Set([stackItem.id]);
    if (isContainer) {
      stack.forEach((s) => {
        if ((s.type === "folder" || s.type === "group") && s.id.startsWith(stackItem.id + "/")) idsToRemove.add(s.id);
      });
      stack.forEach((s) => {
        if (s.type === "report") {
          const parentId = getImmediateParentId(s.id) || s.folderId || null;
          if (parentId && idsToRemove.has(parentId)) idsToRemove.add(s.id);
        }
      });
    }

    // Determine which stack entry (if any) is the one currently being viewed, by content
    // rather than by wsId === routeSelection.workspaceId — a PBI report opened from a
    // custom-app tab routes through /powerBI/report/<pbiWorkspaceId>+<reportId>, where
    // pbiWorkspaceId is the underlying reporting workspace, not this tab's own wsId/_id,
    // so that equality check would always be false for exactly the reports this menu
    // shows as "active". Matching on the route's ids directly (as `active` is computed
    // for the clicked row itself) avoids that mismatch entirely.
    const routeSelection = getRouteSelection(this.props.location?.pathname || "");
    const normFolderPath = routeSelection.folderPath ? routeSelection.folderPath.replace(/\/+$/, "") : "";
    const activeStackItem = stack.find((s) => {
      if (s.type === "report") return Boolean(routeSelection.reportId) && s.id === routeSelection.reportId;
      return Boolean(normFolderPath || routeSelection.groupId) && (s.id === normFolderPath || s.id === routeSelection.groupId);
    });
    const activeId = activeStackItem ? activeStackItem.id : "";
    const shouldNavigate = Boolean(activeId) && idsToRemove.has(activeId);

    idsToRemove.forEach((id) => this.props.removeBreadcrumbItem(wsId, id));

    if (shouldNavigate) {
      const stackIdx = stack.findIndex((s) => s.id === stackItem.id);
      let leftItem = null;
      for (let i = stackIdx - 1; i >= 0; i--) {
        if (!idsToRemove.has(stack[i].id)) {
          leftItem = stack[i];
          break;
        }
      }
      if (leftItem) {
        this.props.navigate(routeForStackItem(leftItem));
      } else {
        this.props.setCustomGroup("");
        this.props.setReportAppStatus(false);
        this.props.setReportGroup(wsId);
        this.props.navigate(`/reports/landing/${wsId}`);
      }
    }
  }
  childRemoveHandler(e, child) {
    const { tabs, selectedTab: selectedTabProp } = this.props;
    let selectedTab = Array.isArray(selectedTabProp) ? selectedTabProp : [-1, -1];
    let rmvIdx = [-1, -1];
    const getActiveCrumbItem = (selection) => {
      const tab = tabs[selection[0]];
      if (!tab || !tab.activeChildren) {
        return null;
      }
      if (selection[3] !== undefined && selection[3] !== -1) {
        return tab.activeChildren?.[selection[1]]?.activeChildren?.[selection[2]]?.activeChildren?.[selection[3]] || null;
      }
      if (selection[2] !== undefined && selection[2] !== -1) {
        return tab.activeChildren?.[selection[1]]?.activeChildren?.[selection[2]] || null;
      }
      if (selection[1] !== undefined && selection[1] !== -1) {
        return tab.activeChildren?.[selection[1]] || null;
      }
      return null;
    };
    const isSameCrumbItem = (a, b) => {
      if (!a || !b) {
        return false;
      }
      if (a.id !== undefined && b.id !== undefined) {
        return a.id === b.id;
      }
      if (a._id !== undefined && b._id !== undefined) {
        return a._id === b._id;
      }
      if (a.path && b.path) {
        return a.path === b.path;
      }
      return false;
    };
    const findChildPath = (nodes, target) => {
      if (!Array.isArray(nodes)) {
        return null;
      }
      for (let idx = 0; idx < nodes.length; idx += 1) {
        const node = nodes[idx];
        if (isSameCrumbItem(node, target)) {
          return [idx];
        }
        const nested = findChildPath(node?.activeChildren || node?.children, target);
        if (nested) {
          return [idx, ...nested];
        }
      }
      return null;
    };
    const activeItemInitial = getActiveCrumbItem(selectedTab);
    const matchPath = findChildPath(tabs?.[selectedTab[0]]?.activeChildren, child);
    if (matchPath && !isSameCrumbItem(child, activeItemInitial)) {
      selectedTab = [selectedTab[0], ...matchPath];
    }
    const activeItem = getActiveCrumbItem(selectedTab);
    const isClosingActive = isSameCrumbItem(child, activeItem);
    const navigateToLeftSibling = () => {
      const tab = tabs[selectedTab[0]];
      if (!tab) {
        return false;
      }
      const isReportLike = (item) =>
        item && (item.rptId || item.id || item._id) && item.fileType !== "folder" && item.type !== "grouping";
      const pickLeftItem = (siblings, leftIndex, baseSelection) => {
        for (let idx = leftIndex; idx >= 0; idx -= 1) {
          const candidate = siblings?.[idx];
          if (!candidate) {
            continue;
          }
          if (isReportLike(candidate)) {
            return { item: candidate, selection: [...baseSelection, idx] };
          }
          if (candidate.fileType === "folder" || candidate.type === "grouping") {
            const inner = candidate.activeChildren || candidate.children || [];
            for (let innerIdx = inner.length - 1; innerIdx >= 0; innerIdx -= 1) {
              const innerCandidate = inner[innerIdx];
              if (isReportLike(innerCandidate)) {
                return { item: innerCandidate, selection: [...baseSelection, idx, innerIdx] };
              }
            }
          }
        }
        return null;
      };

      if (selectedTab[3] !== undefined && selectedTab[3] !== -1) {
        const siblings = tab.activeChildren?.[selectedTab[1]]?.activeChildren?.[selectedTab[2]]?.activeChildren || [];
        const leftIndex = selectedTab[3] - 1;
        const picked = pickLeftItem(siblings, leftIndex, [selectedTab[0], selectedTab[1], selectedTab[2]]);
        if (picked) {
          const leftType = picked.item.type || (picked.item.fileType === "file" ? "powerBI" : picked.item.fileType);
          this.clickHandler(picked.item, picked.selection, leftType, false);
          return true;
        }
        return false;
      }

      if (selectedTab[2] !== undefined && selectedTab[2] !== -1) {
        const siblings = tab.activeChildren?.[selectedTab[1]]?.activeChildren || [];
        const leftIndex = selectedTab[2] - 1;
        const picked = pickLeftItem(siblings, leftIndex, [selectedTab[0], selectedTab[1]]);
        if (picked) {
          const leftType = picked.item.type || (picked.item.fileType === "file" ? "powerBI" : picked.item.fileType);
          this.clickHandler(picked.item, picked.selection, leftType, false);
          return true;
        }
        return false;
      }

      if (selectedTab[1] !== undefined && selectedTab[1] !== -1) {
        const siblings = tab.activeChildren || [];
        const leftIndex = selectedTab[1] - 1;
        const picked = pickLeftItem(siblings, leftIndex, [selectedTab[0]]);
        if (picked) {
          const leftType = picked.item.type || (picked.item.fileType === "file" ? "powerBI" : picked.item.fileType);
          this.clickHandler(picked.item, picked.selection, leftType, false);
          return true;
        }
      }

      return false;
    };
    const navigateToLeftReportInFolder = () => {
      const tab = tabs[selectedTab[0]];
      if (!tab || selectedTab[2] === undefined || selectedTab[2] === -1) {
        return false;
      }
      const isReportItem = (item) =>
        item &&
        (item.type === "powerBI" || item.type === "tableau" || item.type === "staticContent" || item.fileType === "file") &&
        item.fileType !== "folder" &&
        item.type !== "grouping";
      let siblings = tab.activeChildren?.[selectedTab[1]]?.activeChildren || [];
      let baseSelection = [selectedTab[0], selectedTab[1]];

      if (selectedTab[3] !== undefined && selectedTab[3] !== -1) {
        siblings = tab.activeChildren?.[selectedTab[1]]?.activeChildren?.[selectedTab[2]]?.activeChildren || [];
        baseSelection = [selectedTab[0], selectedTab[1], selectedTab[2]];
      }

      for (let idx = (selectedTab[3] ?? selectedTab[2]) - 1; idx >= 0; idx -= 1) {
        const candidate = siblings[idx];
        if (isReportItem(candidate)) {
          const leftType = candidate.type || (candidate.fileType === "file" ? "powerBI" : candidate.fileType);
          this.clickHandler(candidate, [...baseSelection, idx], leftType, false);
          return true;
        }
      }
      return false;
    };
    const navigateToParentFromReport = () => {
      const parentItem = this.props.tabs?.[selectedTab[0]]?.activeChildren?.[selectedTab[1]];
      if (!parentItem) {
        this.props.setCustomGroup("");
        this.props.navigate("/reports/landing/" + this.props.reportGroup);
        this.props.setSelectedTab([selectedTab[0], -1, -1]);
        return;
      }
      if (parentItem.id === undefined) {
        this.props.setCustomGroup(parentItem.path.replace(/\//g, "&+"));
        this.props.navigate(
          "/reports/landing/" + this.props.reportGroup + "/" + encodeURI(parentItem.path).replace(/\//g, "&+"),
        );
      } else {
        this.props.setCustomGroup(parentItem.id);
        this.props.navigate("/reports/landing/" + this.props.reportGroup + "/" + parentItem.id);
      }
      this.props.setSelectedTab([selectedTab[0], selectedTab[1], selectedTab[2]]);
    };
    if (child.type === "grouping") {
      let movedLeft = false;
      const tabForNav = tabs[selectedTab[0]];
      const reportGroupId = this.props.reportGroup || tabForNav?.workspaceId || tabForNav?._id || tabForNav?.id || "";
      const topLevelIdx = tabs[selectedTab[0]]?.activeChildren?.findIndex((item) => {
        if (!item) {
          return false;
        }
        if (item.id !== undefined && child.id !== undefined) {
          return item.id === child.id;
        }
        return false;
      });
      const isActiveGrouping =
        isClosingActive ||
        (topLevelIdx !== -1 &&
          topLevelIdx === selectedTab[1] &&
          (selectedTab[2] === undefined || selectedTab[2] === -1 || !activeItem));

      if (isActiveGrouping) {
        const leftItem = topLevelIdx > 0 ? tabs[selectedTab[0]].activeChildren[topLevelIdx - 1] : null;
        if (leftItem) {
          const leftType = leftItem.type || leftItem.fileType;
          if (leftItem.fileType !== "folder" && leftItem.type !== "grouping") {
            this.props.setCustomGroup("");
          }
          this.clickHandler(leftItem, [selectedTab[0], topLevelIdx - 1], leftType, false);
          movedLeft = true;
        } else {
          movedLeft = navigateToLeftSibling();
        }
        if (!movedLeft) {
          this.props.setSelectedTab([selectedTab[0], -1]);
          this.props.setCustomGroup("");
          if (reportGroupId) {
            this.props.setReportGroup(reportGroupId);
            this.props.navigate("/reports/landing/" + reportGroupId);
          } else {
            this.props.setReportGroup("");
            this.props.navigate("/reports/landing");
          }
        }
      } else if (topLevelIdx !== -1 && topLevelIdx < selectedTab[1]) {
        if (selectedTab[2] !== undefined && selectedTab[2] !== -1) {
          if (selectedTab[3] !== undefined && selectedTab[3] !== -1) {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1, selectedTab[2], selectedTab[3]]);
          } else {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1, selectedTab[2]]);
          }
        } else {
          this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1]);
        }
      }
    } else if (child.type === undefined && child.fileType === "folder") {
      const depth = child.path.split("/").length;
      let movedLeft = false;
      const tabForNav = tabs[selectedTab[0]];
      const reportGroupId = this.props.reportGroup || tabForNav?.workspaceId || tabForNav?._id || tabForNav?.id || "";
      const topLevelIdx = tabs[selectedTab[0]]?.activeChildren?.findIndex((item) => {
        if (!item) {
          return false;
        }
        if (item.id !== undefined && child.id !== undefined) {
          return item.id === child.id;
        }
        if (item.path && child.path) {
          return item.path === child.path;
        }
        return false;
      });

      const isActiveFolder =
        isClosingActive ||
        (topLevelIdx !== -1 &&
          topLevelIdx === selectedTab[1] &&
          (selectedTab[2] === undefined || selectedTab[2] === -1 || !activeItem));

      if (isActiveFolder) {
        const leftItem = topLevelIdx > 0 ? tabs[selectedTab[0]].activeChildren[topLevelIdx - 1] : null;
        if (leftItem) {
          const leftType = leftItem.type || (leftItem.fileType === "file" ? "powerBI" : leftItem.fileType);
          if (leftItem.fileType !== "folder" && leftItem.type !== "grouping") {
            this.props.setCustomGroup("");
          }
          this.clickHandler(leftItem, [selectedTab[0], topLevelIdx - 1], leftType, false);
          movedLeft = true;
        } else {
          movedLeft = navigateToLeftSibling();
        }

        if (!movedLeft) {
          if (depth > 3) {
            let newCgroupArr = child.path.split("/");
            let newCGroup = newCgroupArr[0] + "&+" + newCgroupArr[1] + "&+";
            if (reportGroupId) {
              this.props.setReportGroup(reportGroupId);
              this.props.navigate("/reports/landing/" + reportGroupId + "/" + newCGroup);
            } else {
              this.props.setReportGroup("");
              this.props.navigate("/reports/landing");
            }
            this.props.setCustomGroup(newCGroup);
            this.props.setSelectedTab([selectedTab[0], selectedTab[1], -1, -1]);
          } else {
            if (reportGroupId) {
              this.props.setReportGroup(reportGroupId);
              this.props.navigate("/reports/landing/" + reportGroupId);
            } else {
              this.props.setReportGroup("");
              this.props.navigate("/reports/landing");
            }
            this.props.setCustomGroup("");
            this.props.setSelectedTab([selectedTab[0], -1, -1]);
          }
        }
      } else if (topLevelIdx !== -1 && topLevelIdx < selectedTab[1]) {
        if (selectedTab[2] !== undefined && selectedTab[2] !== -1) {
          if (selectedTab[3] !== undefined && selectedTab[3] !== -1) {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1, selectedTab[2], selectedTab[3]]);
          } else {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1, selectedTab[2]]);
          }
        } else {
          this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1]);
        }
      }
    } else if (selectedTab[2] !== undefined && selectedTab[2] !== -1) {
      if (navigateToLeftReportInFolder()) {
        this.props.removeChild(tabs[selectedTab[0]], child);
        return;
      }
      if (selectedTab[3] !== undefined && selectedTab[3] !== -1) {
        const subFolderChildren = tabs[selectedTab[0]].activeChildren[selectedTab[1]]?.activeChildren?.[selectedTab[2]]?.activeChildren;
        if ((subFolderChildren?.length ?? 0) === 1) {
          navigateToParentFromReport();
        } else {
          if (!navigateToLeftSibling()) {
            navigateToParentFromReport();
          }
        }
      } else {
        let topLevelIdx = -1;
        const folderNode = tabs[selectedTab[0]].activeChildren[selectedTab[1]];
        const folderActiveChildren = folderNode?.activeChildren;
        if (
          folderActiveChildren?.[selectedTab[2]]?._id === child._id &&
          (folderActiveChildren?.length ?? 0) <= 1
        ) {
          if (this.props.tabs[selectedTab[0]].activeChildren[selectedTab[1]].id === undefined) {
            this.props.setCustomGroup(this.props.tabs[selectedTab[0]].activeChildren[selectedTab[1]].path.replace(/\//g, "&+"));
            this.props.navigate(
              "/reports/landing/" +
                this.props.reportGroup +
                "/" +
                encodeURI(this.props.tabs[selectedTab[0]].activeChildren[selectedTab[1]].path).replace(/\//g, "&+"),
            );
          } else {
            this.props.setCustomGroup(this.props.tabs[selectedTab[0]].activeChildren[selectedTab[1]].id);
            this.props.navigate(
              "/reports/landing/" +
                this.props.reportGroup +
                "/" +
                this.props.tabs[selectedTab[0]].activeChildren[selectedTab[1]].id,
            );
          }
        }

        (folderActiveChildren || []).forEach((item, idx) => {
          if (child.id?.includes("+")) {
            let parentId = child.id.split("+")[0];
            let rptId = child.id.split("+")[1];
            let workspaceId;
            try {
              workspaceId = item.workspaceId;
              if (workspaceId === undefined) {
                workspaceId = item.tab.workspaceId;
              }
            } catch (err) {
              console.log(err);
            }
            if (parentId === workspaceId) {
              rmvIdx[0] = idx;
              if (item.activeChildren !== undefined && item.activeChildren.length > 0) {
                item.activeChildren.forEach((child, cIdx) => {
                  if (child._id === rptId) {
                    rmvIdx = [idx, cIdx];
                  }
                });
              }
            }
          }
          if (item.id !== undefined) {
            if (item.id.includes("+")) {
              if (item.id.split("+")[1] === child.id) {
                rmvIdx = [idx];
                // rmvIdx[1] = idx;
              }
            } else if (item.id === child.id) {
              rmvIdx = [idx];
              // rmvIdx[1] = idx;
            }
          } else {
            if (item.path === child.path) {
              rmvIdx = [idx];
              // rmvIdx[1] = idx;
            }
          }
        });

        tabs[selectedTab[0]].activeChildren.forEach((item, idx) => {
          if (item.id !== undefined) {
            if (item.id.includes("+")) {
              if (item.id.split("+")[1] === child.id) {
                topLevelIdx = idx;
              }
            } else if (item.id === child.id) {
              topLevelIdx = idx;
            }
          } else if (child.path !== undefined && item.path === child.path) {
            topLevelIdx = idx;
          }
        });

        //if you are removing the tab you are on and its the first tab
        if (selectedTab[2] === rmvIdx[0] && rmvIdx[0] === 0) {
          if (!navigateToLeftSibling()) {
            navigateToParentFromReport();
          }
        } else if (selectedTab[2] === rmvIdx[0]) {
          if (!navigateToLeftSibling()) {
            navigateToParentFromReport();
          }
        } else if (rmvIdx[0] !== -1 && rmvIdx[0] < selectedTab[2]) {
          this.props.setSelectedTab([selectedTab[0], selectedTab[1], selectedTab[2] - 1]);
        } else if (topLevelIdx !== -1 && topLevelIdx < selectedTab[1]) {
          if (selectedTab[3] !== undefined && selectedTab[3] !== -1) {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1, selectedTab[2], selectedTab[3]]);
          } else {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1, selectedTab[2]]);
          }
        }
      }
    } else if (tabs[selectedTab[0]].activeChildren.length === 1) {
      this.props.setCustomGroup("");
      this.props.setSelectedTab([selectedTab[0], -1, -1]);
      this.props.navigate("/reports/landing/" + this.props.reportGroup, { replace: true });
    } else {
      tabs[selectedTab[0]].activeChildren.forEach((item, idx) => {
        if (child.id?.includes("+")) {
          let parentId = child.id.split("+")[0];
          let rptId = child.id.split("+")[1];
          let workspaceId;
          try {
            workspaceId = item.workspaceId;
            if (workspaceId === undefined) {
              workspaceId = item.tab.workspaceId;
            }
          } catch (err) {
            console.log(err);
          }
          if (parentId === workspaceId) {
            rmvIdx[0] = idx;
            if (item.activeChildren !== undefined && item.activeChildren.length > 0) {
              item.activeChildren.forEach((child, cIdx) => {
                if (child._id === rptId) {
                  rmvIdx = [idx, cIdx];
                }
              });
            }
          }
        }
        if (item.id !== undefined) {
          if (item.id.includes("+")) {
            if (item.id.split("+")[1] === child.id) {
              rmvIdx[0] = idx;
            }
          } else if (item.id === child.id) {
            rmvIdx[0] = idx;
          }
        } else if (child.path !== undefined) {
          let ptPathArr = child.path.split("/");
          let ptPath = ptPathArr[0] + "/" + ptPathArr[1] + "/";
          if (item.path === ptPath) {
            rmvIdx[0] = idx;
          }
          if (ptPathArr.length > 2) {
            item.activeChildren.forEach((gChild, cIdx) => {
              if (gChild.id === child.id) {
                rmvIdx = [idx, cIdx];
              }
            });
          }
        }
      });

      const isClosingReport =
        child?.type === "powerBI" ||
        child?.type === "fileGroupings" ||
        child?.type === "tableau" ||
        child?.type === "staticContent" ||
        child?.fileType === "file";

      //if you are removing the tab you are on and its the first tab
      if (selectedTab[1] === rmvIdx[0] && rmvIdx[0] === 0) {
        if (isClosingReport && navigateToLeftSibling()) {
          this.props.removeChild(tabs[selectedTab[0]], child);
          return;
        }
        this.clickHandler(tabs[selectedTab[0]].activeChildren[1], [selectedTab[0], 0], child.type, false);
      } else if (selectedTab[1] === rmvIdx[0]) {
        if (isClosingReport && navigateToLeftSibling()) {
          this.props.removeChild(tabs[selectedTab[0]], child);
          return;
        }
        //you are removing the tab you are on and its not the first tab
        //the tab we are moving to is a grouping so set the index appropriately
        const leftSibling = tabs[selectedTab[0]].activeChildren[rmvIdx[0] - 1];
        if (!leftSibling) {
          // rmvIdx couldn't be resolved to a valid position (e.g. closing an item that
          // isn't the in-view one) — fall back to the same-siblings/parent navigation
          // used elsewhere in this handler rather than indexing into activeChildren.
          if (!navigateToLeftSibling()) {
            navigateToParentFromReport();
          }
        } else if (leftSibling.type === "grouping") {
          let childIdx = 0;

          if (!leftSibling.children || leftSibling.children.length === 0) {
            //click into the group landing page
            childIdx = -1;
            this.clickHandler(leftSibling, [selectedTab[0], rmvIdx[0] - 1, childIdx], leftSibling.type, false);
          } else {
            //click into the first group report
            this.clickHandler(
              leftSibling,
              [selectedTab[0], rmvIdx[0] - 1, childIdx],
              leftSibling.children[childIdx].type,
              false,
            );
          }
        } else {
          this.clickHandler(leftSibling, [selectedTab[0], rmvIdx[0] - 1], child.type, false);
        }
      } else if (rmvIdx[0] < selectedTab[1]) {
        if (selectedTab[2] !== undefined && selectedTab[2] !== null && selectedTab[2] !== -1) {
          if (rmvIdx[1] < selectedTab[2]) {
            this.props.setSelectedTab([selectedTab[0], selectedTab[1], selectedTab[2] - 1]);
          }
        } else if (rmvIdx[1] === -1) {
          this.props.setSelectedTab([selectedTab[0], selectedTab[1] - 1]);
        }
      }
    }
    // }

    this.props.removeChild(tabs[selectedTab[0]], child);
  }
  renderCloseIcons(child, showDelimiter, showPipe) {
    let delim = ">";
    if (showPipe) {
      delim = "|";
    }

    if (child.id === undefined) {
      if (this.state.showClose === child.path) {
        return (
          <div
            onClick={(e) => {
              this.childRemoveHandler(e, child);
            }}
            className="close-icon-div">
            <CloseReportIcon height={"15px"} viewBox={"0 0 20 20"} color={"#58595B"} />

            {showDelimiter && <div className="delimiter-style delimiter-hover">{delim}</div>}
          </div>
        );
      } else {
        return this.renderDelimiter(showDelimiter, delim);
      }
    } else if (this.state.showClose === child.id) {
      return (
        <div
          onClick={(e) => {
            this.childRemoveHandler(e, child);
          }}
          className="close-icon-div">
          <CloseReportIcon height={"15px"} viewBox={"0 0 20 20"} color={"#58595B"} />

          {showDelimiter && <div className="delimiter-style delimiter-hover">{delim}</div>}
        </div>
      );
    } else {
      return this.renderDelimiter(showDelimiter, delim);
    }
  }
  renderGroupingChilds(children, selectedTab, parentSelected, subFolder = false, routeSelection = {}) {
    let divId;
    let tSelected = false;
    let tSelectedNonState = false;
    let sIdx = 2;
    const routeGroupId = routeSelection?.groupId || "";
    const routeFolderPath = routeSelection?.folderPath || "";
    const routeReportId = routeSelection?.reportId || "";
    const matchesRoute = (item) => {
      if (!item) {
        return false;
      }
      if (routeReportId) {
        const itemId = item.id || item._id || item.rptId || "";
        if (itemId && itemId === routeReportId) {
          return true;
        }
        if (item.id && item.id.includes("+") && item.id.split("+")[1] === routeReportId) {
          return true;
        }
      }
      if (routeGroupId && (item.id === routeGroupId || item._id === routeGroupId)) {
        return true;
      }
      if (routeFolderPath && item.path) {
        return encodePathKey(item.path) === encodePathKey(routeFolderPath);
      }
      return false;
    };

    return children.map((child, idx) => {
      let showDelimiter = false;
      if (children.length - 1 === idx) {
        showDelimiter = true;
      }

      if (parentSelected) {
        if (selectedTab[sIdx] === undefined || selectedTab[sIdx] === -1)
          if (idx === selectedTab[sIdx]) {
            tSelected = true;
          } else {
            tSelected = false;
          }
        else if (child.activeChildren && child.activeChildren.length > 0) {
          if (idx === selectedTab[sIdx]) {
            tSelectedNonState = true;
          }
          tSelected = false;
        } else if (idx === selectedTab[sIdx]) {
          if (subFolder) {
            if (idx === selectedTab[sIdx + 1]) {
              tSelected = true;
            } else {
              tSelected = false;
            }
          } else {
            if (idx === selectedTab[sIdx]) {
              tSelected = true;
            } else {
              tSelected = false;
            }
          }
        } else if (idx === selectedTab[sIdx + 1]) {
          tSelected = true;
        } else {
          tSelected = false;
        }
      }

      const routeSelected = matchesRoute(child);
      if (child.fileType === "file" || child.activeChildren === undefined || child.activeChildren.length === 0) {
        divId = child.id;
        if (divId === undefined) {
          divId = child.path;
        }
        return (
          <div
            className={"grouping-childs" + idx}
            onMouseEnter={(e) => {
              if (child.id === undefined) {
                this.setState({ showClose: child.path });
              } else {
                this.setState({ showClose: child.id });
              }
            }}
            onMouseLeave={(e) => {
              this.setState({ showClose: -1 });
            }}
            key={divId + "file"}
            style={{ display: "flex", flexDirection: "row" }}>
            <div
              style={{
                paddingLeft: "20px",
                display: "flex",
                flexDirection: "row",
                cursor: "pointer",
              }}
              data-testid={this.breadcrumbTestId(child)}
              className={tSelected || routeSelected ? "hover" : "child-selected"}
              onClick={(evt) => {
                if (child.path !== undefined && child.path.split("/").length === 4 && child.path.split("/")[3] !== "") {
                  var fIdx = -1;
                  let firstIdx = selectedTab[1];

                  this.props.tabs[selectedTab[0]].activeChildren.forEach((item, idx) => {
                    if (item.name === child.path.split("/")[1]) {
                      firstIdx = idx;
                    }
                  });
                  this.props.tabs[selectedTab[0]].activeChildren[firstIdx].activeChildren.forEach((item, idx) => {
                    if (item.name === child.path.split("/")[2]) {
                      fIdx = idx;
                    }
                  });

                  this.clickHandler(child, [selectedTab[0], firstIdx, fIdx, idx], child.type, false);
                } else if (child.path !== undefined && child.path.split("/").length === 4) {
                  var kIdx = -1;
                  this.props.tabs[selectedTab[0]].activeChildren.forEach((item, idx) => {
                    if (item.name === child.path.split("/")[1]) {
                      kIdx = idx;
                    }
                  });

                  this.clickHandler(child, [selectedTab[0], kIdx, idx], child.type, false);
                } else if (child.index !== selectedTab[1]) {
                  this.clickHandler(child, [selectedTab[0], child.index, idx], child.type, false);
                }
              }}>
              {child.title}
            </div>
            {this.renderCloseIcons(child, showDelimiter, true)}
          </div>
        );
      } else {
        divId = child.id;
        if (divId === undefined) {
          divId = child.path;
        }
        return (
          <div
            className="folderDiv"
            onMouseEnter={(e) => {
              if (child.id === undefined) {
                this.setState({ showClose: child.path });
              } else {
                this.setState({ showClose: child.id });
              }
            }}
            onMouseLeave={(e) => {
              this.setState({ showClose: -1 });
            }}
            key={divId + "folder"}
            style={{ display: "flex", flexDirection: "row" }}>
            <div
              style={{
                paddingLeft: "20px",
                display: "flex",
                flexDirection: "row",
                cursor: "pointer",
              }}
              data-testid={this.breadcrumbTestId(child)}
              className={tSelected || routeSelected ? "hover" : "child-selected"}
              onClick={(evt) => {
                if (child.index !== selectedTab[1]) {
                  //find subfolder id
                  let parentIdx = 0;
                  let compareStr = child.path.split("/")[1];
                  this.props.tabs[selectedTab[0]].activeChildren.forEach((item, idx) => {
                    if (item.name === compareStr) {
                      parentIdx = idx;
                    }
                  });
                  let cType = child.type;
                  if (cType === undefined) {
                    cType = child.fileType;
                  }
                  this.clickHandler(child, [selectedTab[0], parentIdx, idx], cType, false);
                }
              }}>
              {child.title}
            </div>
            {this.renderCloseIcons(child, showDelimiter, false)}
            {this.renderGroupingChilds(child.activeChildren, selectedTab, tSelectedNonState, true, routeSelection)}
          </div>
        );
      }
    });
  }
  renderDelimiter(render, delimiter) {
    if (render) {
      return <div className="delimiter-base">{delimiter}</div>;
    } else {
      return <div className="delimiter-base"></div>;
    }
  }

  /**
   * Render breadcrumbs for a custom-app workspace from an explicit ordered stack.
   * @param {Array<{type:'report'|'group'|'folder', id:string}>} stack
   * @param {string} wsId  - custom-app workspace _id (used for landing routes)
   * @param {string} activeItemId - id of the currently-active breadcrumb item;
   *   pass empty string when App Home is the current page (no item active)
   */
  renderBreadcrumbStack(stack, wsId, activeItemId) {
    if (!stack || stack.length === 0) return null;

    const { workspaces } = this.props;
    const index = workspaces?.length ? getReportingIndex(workspaces) : null;

    // Returns true for a 36-character UUID (PowerBI workspace GUID).
    const isPbiGuid = (v) => typeof v === "string" && v.length === 36;

    const routeFor = (item) => {
      if (item.type === "report") {
        const entry = index?.nodeById?.[item.id];
        const reportNode = entry?.node;
        // Static content reports use a different URL scheme.
        if (reportNode?.type === "staticContent") {
          const scWs = entry?.workspace;
          const scWsId = scWs?.workspaceId || scWs?._id || wsId;
          return `/sc/report/${scWsId}+${item.id}`;
        }
        // Prefer a proper 36-char PBI workspace GUID; fall back progressively.
        const pbiWsId =
          (isPbiGuid(entry?.workspace?.workspaceId) ? entry.workspace.workspaceId : null) ||
          (isPbiGuid(reportNode?.workspaceId) ? reportNode.workspaceId : null) ||
          (isPbiGuid(wsId) ? wsId : null) ||
          entry?.workspace?.workspaceId ||
          entry?.workspace?._id ||
          wsId;
        return `/powerBI/report/${pbiWsId}+${item.id}`;
      }
      if (item.type === "folder") {
        // Encode each path segment with encodeURIComponent for URL safety, then
        // join with "&+". A trailing "&+" ensures parseReportRoute always routes
        // this through the folderPath branch (decoded by decodeURIComponent)
        // rather than the groupId branch, which prevents %20/space mismatches.
        const encoded = item.id
          .split("/")
          .filter(Boolean)
          .map((s) => encodeURIComponent(s))
          .join("&+");
        return `/reports/landing/${wsId}/${encoded}&+`;
      }
      // group
      return `/reports/landing/${wsId}/${item.id}`;
    };

    // Returns true when workspaces haven't loaded yet — index will be null.
    const indexLoading = !index;

    // Resolve activeItemId to canonical stack form.
    // Handles the case where the URL carries a MongoDB ObjectId as the folder segment
    // but the stack stores the resolved path (e.g. "Marketing").
    const resolvedActiveItemId = (() => {
      if (!activeItemId) return "";
      // Already matches something in the stack — use as-is.
      if (stack.some((i) => i.id === activeItemId)) return activeItemId;
      // Try URI-decoded version (handles %20-encoded spaces from browser URL normalisation).
      const decoded = (() => {
        try {
          return decodeURIComponent(activeItemId);
        } catch {
          return activeItemId;
        }
      })();
      if (decoded !== activeItemId && stack.some((i) => i.id === decoded)) return decoded;
      // Try to resolve via nodeById (originally for folder ObjectId → path resolution).
      const node = index?.nodeById?.[activeItemId]?.node;
      if (node) {
        // Folder ObjectId → path: only apply when the resolved path is actually in
        // the stack. Report nodes also have a path ("WS/Folder/ReportTitle") but that
        // is never stored as a stack item id — blindly returning it was preventing
        // the cross-ID fallback below from running.
        if (node.path) {
          const resolved = node.path.replace(/\/+$/, "");
          if (stack.some((i) => i.id === resolved)) return resolved;
        }
        // Cross-ID resolution for reports: pbiGuid ↔ Mongo _id.
        // Handles the case where the URL was built with the Mongo _id (e.g. subfolder
        // reports whose ws.reports entry lacks rptId) but the chip was pushed with
        // the PBI GUID (from the original tile navigation).
        const altIds = [node.rptId, node._id, node.id].filter((id) => id && id !== activeItemId);
        const altMatch = altIds.find((altId) => stack.some((i) => i.id === altId));
        if (altMatch) return altMatch;
      }
      return activeItemId;
    })();

    const labelFor = (item) => {
      if (item.type === "report") {
        // New report being created (URL uses "new" as the placeholder ID).
        if (item.id === "new") return "New Report";
        const node = index?.nodeById?.[item.id]?.node;
        // Just-saved report: real ID is in the stack but workspaces haven't reloaded yet.
        if (!node && this.state.savingReportId === item.id) return "Saving...";
        return node?.title || node?.name || item.id;
      }
      if (item.type === "folder") {
        const node = index?.nodeByPath?.[encodePathKey(item.id)]?.node;
        const folderName = node?.title || node?.name || item.id.split("/").filter(Boolean).slice(-1)[0];
        // If we resolved something meaningful (not just the raw path), return it.
        if (folderName && folderName !== item.id) return folderName;
        // Otherwise show ellipsis while loading to avoid flashing the raw path.
        return indexLoading ? "..." : folderName || item.id;
      }
      // First try the reporting index.
      const node = index?.nodeById?.[item.id]?.node;
      if (node?.title || node?.name) return node.title || node.name;
      // Index miss — scan the workspace groupings directly. This handles cases where
      // the grouping's id field isn't a standard key in the reporting index
      // (e.g. custom UUID-style ids that differ from the Mongoose _id).
      const allWsPool = [...(this.props.workspaces || []), ...(this.props.tabs || [])];
      const appWsForLabel = allWsPool.find(
        (w) => w?.type === "customApplication" && (w._id === wsId || w.workspaceId === wsId),
      );
      // Check both groupings and children (the re-ordered copy used by reportList).
      const groupNode =
        appWsForLabel?.groupings?.find((g) => g.id === item.id || String(g._id) === item.id) ||
        appWsForLabel?.children?.find((g) => g.id === item.id || String(g._id) === item.id);
      if (groupNode?.title || groupNode?.name) return groupNode.title || groupNode.name;
      // Can't resolve the name — show ellipsis instead of a raw GUID/hex string.
      // Once workspaces load and the index populates, the next render shows the real name.
      return "...";
    };

    /**
     * Return the immediate parent ID for a report item — works for both workspace types:
     * – Custom-app workspace: returns the group ID (from workspace.groupings).
     * – Regular workspace: returns the normalised folder path (from reportNode.path / pathIds).
     * Returns null for root-level reports.
     */
    const getImmediateParentId = (reportId) => {
      const ws = workspaces?.find((w) => (w._id || w.workspaceId) === wsId);
      // Custom app — scan groupings
      if (ws?.groupings) {
        for (const g of ws.groupings) {
          const kids = g.children || [];
          if (kids.some((c) => c.rptId === reportId || c.id === reportId || c._id === reportId)) {
            return g.id || g._id || null;
          }
        }
      }
      // Regular workspace — derive parent folder from the report node
      const reportNode = index?.nodeById?.[reportId]?.node;
      if (reportNode) {
        const reportKey = reportNode.id || reportNode._id || reportNode.rptId || "";
        const rawPathIds = reportNode.pathIds ? String(reportNode.pathIds) : "";
        const pathIds = rawPathIds
          .split("/")
          .filter(Boolean)
          .filter((id) => id !== reportKey);
        if (pathIds.length > 0) {
          const deepestFolderId = pathIds[pathIds.length - 1];
          const folderNode = index?.nodeById?.[deepestFolderId]?.node;
          if (folderNode?.path) return folderNode.path.replace(/\/+$/, "");
        }
        const rawPath = reportNode.path || reportNode.fullPath || "";
        if (rawPath) {
          const segments = rawPath.replace(/\/+$/, "").split("/").filter(Boolean);
          const rptTitle = reportNode.title || reportNode.name || "";
          if (rptTitle && segments[segments.length - 1] === rptTitle) segments.pop();
          if (segments.length > 0) return segments.join("/");
        }
      }
      return null;
    };

    // A report that ends up directly after a group/folder node needs a leading "|"
    // UNLESS it is a natural child of that node (parent ID === prev.id).
    // Note: this is evaluated per chip-scoped items array (chipItems), not the global stack.
    const prefixDelimFor = (item, chipItems, chipIdx) => {
      if (item.type !== "report") return null;
      if (chipIdx === 0) return null;
      const prev = chipItems[chipIdx - 1];
      if (!prev || prev.type === "report") return null;
      const parentId = getImmediateParentId(item.id);
      // Suppress when parent is confirmed to be the preceding folder/group.
      if (parentId && parentId === prev.id) return null;
      // Suppress when the item carries an explicit folderId (set eagerly by
      // handleClose/cloneReport before the index updates) that matches the
      // preceding folder/group — avoids a spurious pipe in save-as/new-report flow.
      if (!parentId && item.folderId && item.folderId === prev.id) return null;
      return <div className="delimiter-style bc-delim-pipe delimiter-base">|</div>;
    };

    // Maps a delimiter character to its CSS class for the chip design.
    const delimClass = (d) => {
      if (d === "\u203a") return "delimiter-style bc-delim-chevron";
      if (d === "·") return "delimiter-style bc-delim-dot";
      return "delimiter-style bc-delim-pipe";
    };

    // ── Group stack items into bare-report segments and chip scopes ──────────────
    // A chip scope is anchored by a top-level folder/group (not a path-descendant
    // of any other folder/group already in the stack).
    // Reports whose immediate parent resolves to a chip anchor (or is a path
    // sub-folder of it) belong to that chip; all others are bare root reports.
    // An item may also carry an explicit `folderId` field (set by handleClose /
    // cloneReport before the index updates) which is used when parentId is null.
    const renderGroups = (() => {
      const groups = []; // { type: "bare"|"chip", anchorId?: string, items: [] }
      for (const item of stack) {
        if (item.type === "folder" || item.type === "group") {
          const parentChip = groups.find((g) => g.type === "chip" && item.id.startsWith(g.anchorId + "/"));
          if (parentChip) {
            parentChip.items.push(item);
          } else {
            groups.push({ type: "chip", anchorId: item.id, items: [item] });
          }
        } else {
          // report
          const parentId = getImmediateParentId(item.id);
          // Use the explicit folderId stamp when the index hasn't resolved yet.
          const effectiveParentId = parentId || item.folderId || null;
          const ownerChip =
            effectiveParentId &&
            groups.find(
              (g) => g.type === "chip" && (effectiveParentId === g.anchorId || effectiveParentId.startsWith(g.anchorId + "/")),
            );
          if (ownerChip) {
            ownerChip.items.push(item);
          } else {
            const last = groups[groups.length - 1];
            if (last && last.type === "bare") {
              last.items.push(item);
            } else {
              groups.push({ type: "bare", items: [item] });
            }
          }
        }
      }
      return groups;
    })();

    // ── Render a single stack item (used for both bare and chip groups) ───────────
    const renderStackItem = (item, scopeItems, scopeIdx) => {
      const isActive = Boolean(resolvedActiveItemId) && item.id === resolvedActiveItemId;
      const label = labelFor(item);

      // Suppress folder/group chips while the name hasn't resolved yet — avoids
      // flashing raw GUIDs/hex IDs during the initial workspace-loading render.
      const isFolder = item.type === "folder" || item.type === "group";
      if (isFolder && label === "...") return null;
      const route = routeFor(item);
      const node =
        item.type === "folder"
          ? index?.nodeByPath?.[encodePathKey(item.id)]?.node || { path: item.id }
          : index?.nodeById?.[item.id]?.node || { id: item.id };
      const keyBase = item.id.replace(/[^a-zA-Z0-9-]/g, "_");
      const derivedKey = `${keyBase}-${label}`;
      const showClose = this.state.showClose === derivedKey;

      const onClick = () => this.props.navigate(route);

      const onClose = () => {
        const isContainer = item.type === "folder" || item.type === "group";
        const idsToRemove = new Set([item.id]);
        if (isContainer) {
          stack.forEach((s) => {
            if ((s.type === "folder" || s.type === "group") && s.id.startsWith(item.id + "/")) idsToRemove.add(s.id);
          });
          stack.forEach((s) => {
            if (s.type === "report") {
              // Use the explicit folderId stamp (set by handleClose/cloneReport before
              // the index updates) as fallback when getImmediateParentId returns null.
              const parentId = getImmediateParentId(s.id) || s.folderId || null;
              if (parentId && idsToRemove.has(parentId)) idsToRemove.add(s.id);
            }
          });
        }
        const activeId = resolvedActiveItemId;
        const shouldNavigate = Boolean(activeId) && idsToRemove.has(activeId);
        idsToRemove.forEach((id) => this.props.removeBreadcrumbItem(wsId, id));
        if (shouldNavigate) {
          const stackIdx = stack.findIndex((s) => s.id === item.id);
          let leftItem = null;
          for (let i = stackIdx - 1; i >= 0; i--) {
            if (!idsToRemove.has(stack[i].id)) {
              leftItem = stack[i];
              break;
            }
          }
          if (leftItem) {
            this.props.navigate(routeFor(leftItem));
          } else {
            this.props.setCustomGroup("");
            this.props.setReportAppStatus(false);
            this.props.setReportGroup(wsId);
            this.props.navigate(`/reports/landing/${wsId}`);
          }
        }
      };

      // Trailing delimiter — based on the NEXT item in the same scope array.
      // Last item has no trailing delimiter.
      const nextItem = scopeItems ? scopeItems[scopeIdx + 1] : null;
      const trailingDelim = (() => {
        if (!nextItem) return null;
        if (item.type !== "report") return "\u203a";
        if (nextItem.type === "report") {
          const thisParent = getImmediateParentId(item.id);
          const nextParent = getImmediateParentId(nextItem.id);
          if (thisParent !== null && thisParent === nextParent) return "·";
        }
        return "|";
      })();

      const labelClass = isFolder
        ? isActive
          ? "bc-folder-label bc-folder-active child-selected-div"
          : "bc-folder-label child-selected-div"
        : isActive
          ? "bc-report-active child-selected-div"
          : "bc-report-label child-selected-div";
      const xColor = labelClass.includes("bc-report-active") ? "#fff" : "#58595B";

      return (
        <div
          key={derivedKey}
          className="CSD-Stuff"
          style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          onMouseEnter={() => this.setState({ showClose: derivedKey })}
          onMouseLeave={() => this.setState({ showClose: -1 })}>
          {prefixDelimFor(item, scopeItems, scopeIdx)}
          <OverlayTrigger placement="top" overlay={<Tooltip id={`bc-tip-${derivedKey}`}>{label}</Tooltip>}>
            <div
              style={{
                paddingLeft: "6px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "3px",
                position: "relative",
                cursor: !isActive || isFolder ? "pointer" : "default",
              }}
              data-testid={
                // For folder/group items use the canonical stack id (item.id) rather
                // than the node's _id or path so the testid is stable and predictable
                // and always matches breadcrumbIdFromPathSegment in E2E tests.
                isFolder ? `breadcrumb-item-${keyBase}` : this.breadcrumbTestId(node)
              }
              className={labelClass}
              onClick={onClick}>
              {showClose ? (
                <span
                  title="Close"
                  className="bc-close-btn"
                  style={{ display: "inline-flex", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}>
                  <CloseReportIcon height={"13px"} viewBox={"0 0 20 20"} color={xColor} />
                </span>
              ) : isFolder && (item.type === "group" || scopeIdx === 0) ? (
                <BcFolderIcon />
              ) : isFolder ? (
                <BcSubfolderIcon />
              ) : (
                <BcDocIcon />
              )}
              {/* <span className="bc-label-text" style={isFolder ? undefined : { paddingRight: "6px" }}> */}
              <BcLabelText applyFix={!isFolder}>{label}</BcLabelText>
            </div>
          </OverlayTrigger>
          {trailingDelim && <div className={`${delimClass(trailingDelim)} delimiter-base`}>{trailingDelim}</div>}
        </div>
      );
    };

    // ── Render each group ─────────────────────────────────────────────────────
    const renderedGroups = renderGroups.map((group, gIdx) => {
      const gKey = `grp-${gIdx}`;
      if (group.type === "bare") {
        return (
          <div key={gKey} className="bc-bare-group">
            {group.items.map((item, i) => renderStackItem(item, group.items, i))}
          </div>
        );
      }
      return (
        <div key={gKey} className="bc-chip">
          {group.items.map((item, i) => renderStackItem(item, group.items, i))}
        </div>
      );
    });

    if (renderedGroups.length === 0) return null;
    return <div className="bc-row">{renderedGroups}</div>;
  }

  renderDerivedSubTabs(routeSelection = {}) {
    const { workspaces } = this.props;
    const pathname = this.props.location?.pathname || "";
    const { selection, node, report, index } = getRouteContext(pathname, workspaces || []);
    const activeSelection = routeSelection?.workspaceId ? routeSelection : selection;
    const { groupId, folderPath, reportId } = activeSelection;
    const anchorReportId = activeSelection?.anchorReportId || "";
    const suppressActive = Boolean(routeSelection?.suppressActive);
    const appendAfterReport = Boolean(activeSelection?.appendAfterReport);
    const selectedNode = folderPath
      ? index?.nodeByPath?.[encodePathKey(folderPath)]?.node || node || null
      : groupId
        ? index?.nodeById?.[groupId]?.node || node || null
        : node || null;
    const reportEntry = reportId ? index?.nodeById?.[reportId] : null;
    const workspaceFromSelection = activeSelection?.workspaceId ? index?.workspaceById?.[activeSelection.workspaceId] : null;
    const embedWorkspaceId = activeSelection?.workspaceId || report?.workspaceId || reportEntry?.workspace?.workspaceId || "";
    const findCustomAppForReport = (reportKey, workspaces = []) => {
      if (!reportKey || !Array.isArray(workspaces)) {
        return null;
      }
      for (const ws of workspaces) {
        if (ws?.type !== "customApplication" || !Array.isArray(ws.groupings)) {
          continue;
        }
        for (const group of ws.groupings) {
          const children = Array.isArray(group?.children) ? group.children : [];
          if (children.some((child) => child?.id === reportKey || child?._id === reportKey || child?.rptId === reportKey)) {
            return ws;
          }
        }
      }
      return null;
    };
    const customAppWorkspace = reportId ? findCustomAppForReport(reportId, workspaces || []) : null;
    const breadcrumbWorkspaceId =
      customAppWorkspace?._id ||
      (reportEntry?.workspace?.type === "customApplication"
        ? reportEntry?.workspace?._id
        : workspaceFromSelection?.workspaceId ||
          workspaceFromSelection?._id ||
          reportEntry?.workspace?.workspaceId ||
          reportEntry?.workspace?._id ||
          activeSelection?.workspaceId ||
          report?.workspaceId ||
          "");
    const resolvedWorkspaceId = breadcrumbWorkspaceId;

    if (!resolvedWorkspaceId || (!selectedNode && !report && !reportId && !folderPath && !groupId)) {
      return null;
    }
    const reportNode = report || reportEntry?.node || null;
    const anchorReportEntry = anchorReportId ? index?.nodeById?.[anchorReportId] : null;
    const anchorReportNode = anchorReportEntry?.node || null;
    if (reportId && !reportNode) {
      return null;
    }

    const getReportFolderPaths = (reportNode) => {
      if (!reportNode) {
        return [];
      }
      const rawPath = reportNode.path || reportNode.fullPath || (Array.isArray(reportNode.paths) ? reportNode.paths[0] : "");
      if (!rawPath) {
        return [];
      }
      const trimmed = rawPath.endsWith("/") ? rawPath.slice(0, -1) : rawPath;
      const segments = trimmed.split("/").filter(Boolean);
      const workspaceName = (activeSelection?.workspaceId && index?.workspaceById?.[activeSelection.workspaceId]?.name) || "";
      if (workspaceName && segments[0] && segments[0].toLowerCase() === workspaceName.toLowerCase()) {
        segments.shift();
      }
      const reportName = reportNode.title || reportNode.name || "";
      if (reportName && segments[segments.length - 1] === reportName) {
        segments.pop();
      }
      const folderPaths = [];
      let current = "";
      segments.forEach((segment) => {
        current = current ? `${current}/${segment}/` : `${segment}/`;
        folderPaths.push(current);
      });
      return folderPaths;
    };

    const getReportFolderIds = (reportNode) => {
      const raw = reportNode?.pathIds;
      if (!raw) {
        return [];
      }
      const reportKey = reportNode.id || reportNode._id || reportNode.rptId || "";
      return String(raw)
        .split("/")
        .filter(Boolean)
        .filter((id) => id !== reportKey);
    };

    const findGroupForReport = (workspace, reportKey) => {
      if (!workspace || !reportKey) {
        return null;
      }
      const groupings = Array.isArray(workspace.groupings) ? workspace.groupings : [];
      for (const group of groupings) {
        if (!Array.isArray(group?.children)) {
          continue;
        }
        const found = group.children.find(
          (child) => child?.id === reportKey || child?._id === reportKey || child?.rptId === reportKey,
        );
        if (found) {
          return group;
        }
      }
      return null;
    };

    const scopeCrumbs = [];
    const anchorCrumbs = [];
    let hasFolderScope = false;
    const makeCrumb = (item, titleOverride, onClick, onClose, delimiter = "|", isActive = false, isNested = false) => {
      const title = titleOverride || item?.title || item?.name || "";
      const keyBase = item?.id || item?._id || item?.path || `${resolvedWorkspaceId}-${String(title).toLowerCase()}`;
      const derivedKey = `${keyBase}-${title}`;
      const showClose = this.state.showClose === derivedKey;
      // Distinguish structural (folder/group) crumbs from report crumbs by delimiter character.
      const isFolder = delimiter === "›" || delimiter === ">";
      const delimCls = (d) => {
        if (d === "›" || d === ">") return "delimiter-style bc-delim-chevron";
        if (d === "·") return "delimiter-style bc-delim-dot";
        return "delimiter-style bc-delim-pipe";
      };
      const labelClass = isFolder
        ? isActive
          ? "bc-folder-label bc-folder-active child-selected-div"
          : "bc-folder-label child-selected-div"
        : isActive
          ? "bc-report-active child-selected-div"
          : "bc-report-label child-selected-div";
      const xColor = labelClass.includes("bc-report-active") ? "#fff" : "#58595B";
      return (
        <div
          key={derivedKey}
          className="CSD-Stuff"
          style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          onMouseEnter={() => {
            this.setState({ showClose: derivedKey });
          }}
          onMouseLeave={() => {
            this.setState({ showClose: -1 });
          }}>
          <OverlayTrigger placement="top" overlay={<Tooltip id={`bc-tip-${derivedKey}`}>{title}</Tooltip>}>
            <div
              style={{
                paddingLeft: "6px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "3px",
                position: "relative",
                cursor: onClick ? "pointer" : "default",
              }}
              data-testid={this.breadcrumbTestId(item)}
              className={labelClass}
              onClick={onClick}>
              {showClose && onClose ? (
                <span
                  title="Close"
                  className="bc-close-btn"
                  style={{ display: "inline-flex", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}>
                  <CloseReportIcon height={"13px"} viewBox={"0 0 20 20"} color={xColor} />
                </span>
              ) : isFolder ? (
                isNested ? (
                  <BcSubfolderIcon />
                ) : (
                  <BcFolderIcon />
                )
              ) : (
                <BcDocIcon />
              )}
              {/* <span className="bc-label-text" style={isFolder ? undefined : { paddingRight: "6px" }}> */}
              <BcLabelText applyFix={!isFolder}>{title}</BcLabelText>
            </div>
          </OverlayTrigger>
          {delimiter && <div className={`${delimCls(delimiter)} delimiter-base`}>{delimiter}</div>}
        </div>
      );
    };

    const selectionCrumbs = [];
    const pushSelectionCrumbs = ({ activeLast = false, onCloseLast = null } = {}) => {
      if (folderPath) {
        const sourcePath = selectedNode?.path || folderPath;
        const segments = String(sourcePath).split("/").filter(Boolean);
        const workspaceName = workspaceFromSelection?.name || "";
        if (workspaceName && segments[0] && segments[0].toLowerCase() === workspaceName.toLowerCase()) {
          segments.shift();
        }
        let currentPath = "";
        segments.forEach((segment, idx) => {
          currentPath = currentPath ? `${currentPath}/${segment}/` : `${segment}/`;
          const pathKey = encodePathKey(currentPath);
          const folderNode = index?.nodeByPath?.[pathKey]?.node || { path: currentPath, title: segment, name: segment };
          const isLast = idx === segments.length - 1;
          selectionCrumbs.push(
            makeCrumb(
              folderNode,
              folderNode?.title || folderNode?.name || segment,
              () => {
                this.props.setCustomGroup(pathKey);
                this.props.setReportGroup(resolvedWorkspaceId);
                this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${pathKey}`);
              },
              isLast ? onCloseLast : null,
              "›",
              isLast && activeLast,
              idx > 0,
            ),
          );
        });
      } else if (groupId) {
        const groupNode = selectedNode || index?.nodeById?.[groupId]?.node;
        // Find the group directly in workspace groupings (catches index misses).
        const allWsForDerived = [...(this.props.workspaces || []), ...(this.props.tabs || [])];
        const appWsForDerived = allWsForDerived.find(
          (w) => w?.type === "customApplication" && (w._id === resolvedWorkspaceId || w.workspaceId === resolvedWorkspaceId),
        );
        const directGroupNode = appWsForDerived?.groupings?.find((g) => g.id === groupId || String(g._id) === groupId);
        const resolvedGroupNode = groupNode || directGroupNode;
        const groupLabel = resolvedGroupNode?.title || resolvedGroupNode?.name;
        // If the name isn't available yet (workspaces still loading), suppress the
        // chip entirely — avoids flashing the raw GUID in the breadcrumb bar.
        if (!groupLabel) return;
        selectionCrumbs.push(
          makeCrumb(
            resolvedGroupNode,
            groupLabel,
            () => {
              this.props.setCustomGroup(groupId);
              this.props.setReportGroup(resolvedWorkspaceId);
              this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${groupId}`);
            },
            onCloseLast,
            "›",
            activeLast,
          ),
        );
      }
    };

    pushSelectionCrumbs({
      activeLast: !reportId && !suppressActive,
      onCloseLast: () => {
        // If we have an anchor report (reportA | groupA and closing groupA),
        // navigate to the anchor report and show it as active.
        if (appendAfterReport && anchorReportId) {
          const anchorWsId =
            anchorReportNode?.workspaceId ||
            anchorReportEntry?.workspace?.workspaceId ||
            anchorReportEntry?.workspace?._id ||
            embedWorkspaceId ||
            "";
          if (anchorWsId && anchorReportId) {
            // Store a plain marker so componentDidUpdate does NOT re-attach the
            // group context when it sees the landing→powerBI route transition.
            this.props.setBreadcrumbSelection(resolvedWorkspaceId, {
              workspaceId: resolvedWorkspaceId,
              reportId: anchorReportId,
              groupId: "",
              folderPath: "",
              _skipAppend: true,
            });
            this.props.navigate(`/powerBI/report/${anchorWsId}+${anchorReportId}`);
            return;
          }
        }
        this.handleDerivedBreadcrumbClose(activeSelection);
      },
    });

    // Always push the selection chain (group/folder) before the report crumb.
    // For non-append mode this is the only breadcrumb; for append mode it sits
    // between the anchor report (unshifted below) and the new report.
    scopeCrumbs.push(...selectionCrumbs);
    if (selectionCrumbs.length > 0) hasFolderScope = true;

    const hasSelectionCrumbs = selectionCrumbs.length > 0;

    // Determine if the anchor report is a sibling of the current report within
    // the same group (e.g. both reportA and reportB are under groupA).
    //   same-group  → groupA > reportA | reportB   (push anchor between group and active report)
    //   cross-group → reportA | groupA > reportB   (unshift anchor before group)
    // When on the group page (no reportId) the anchor is always rendered inline
    // after the group crumb regardless of group membership.
    const anchorInSameGroupCheck = () => {
      // No active report → we're on the group page; treat anchor as same-group
      // sibling so it renders inline: groupA > reportA (active: groupA).
      if (!reportId) return true;
      const anchorKey = anchorReportEntry?.node?.rptId || anchorReportEntry?.node?.id || anchorReportId;
      const ws = customAppWorkspace || workspaceFromSelection || null;
      return findGroupForReport(ws, anchorKey)?.id === groupId;
    };
    const anchorIsInSameGroup =
      Boolean(anchorReportId) && anchorReportId !== reportId && Boolean(anchorReportNode) && Boolean(groupId)
        ? anchorInSameGroupCheck()
        : false;

    // Same-group case: push anchor sibling before the active report.
    if (anchorIsInSameGroup) {
      const anchorTitle = anchorReportNode.title || anchorReportNode.name || anchorReportId;
      const anchorWsId = anchorReportNode.workspaceId || anchorReportEntry?.workspace?.workspaceId || embedWorkspaceId || "";
      scopeCrumbs.push(
        makeCrumb(
          anchorReportNode,
          anchorTitle,
          () => {
            if (anchorWsId) {
              this.props.navigate(`/powerBI/report/${anchorWsId}+${anchorReportId}`);
            }
          },
          null,
          "·",
          false,
        ),
      );
    }

    if (reportNode) {
      let parentFolderPath = "";
      if (reportNode.path && reportNode.path.includes("/")) {
        const parts = reportNode.path.split("/").filter(Boolean);
        if (parts.length > 1) {
          parentFolderPath = `${parts.slice(0, -1).join("/")}/`;
        }
      }

      const reportKey = reportNode.rptId || reportNode.id || reportNode._id || reportId || "";
      const workspaceForGroup = customAppWorkspace || reportEntry?.workspace || selection?.workspace || null;
      const groupFallback = findGroupForReport(workspaceForGroup, reportKey);
      const groupIdFromReport = reportNode.groupId || groupFallback?.id || "";
      const folderIds = getReportFolderIds(reportNode);
      let folderPaths = [];
      if (!(appendAfterReport && hasSelectionCrumbs)) {
        if (groupIdFromReport) {
          const groupNode = index?.nodeById?.[groupIdFromReport]?.node || groupFallback || { id: groupIdFromReport };
          const groupTitle = groupNode?.title || groupNode?.name || groupIdFromReport;
          scopeCrumbs.push(
            makeCrumb(
              groupNode,
              groupTitle,
              () => {
                this.props.setCustomGroup(groupIdFromReport);
                this.props.setReportGroup(resolvedWorkspaceId);
                this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${groupIdFromReport}`);
              },
              null,
              "›",
              false,
            ),
          );
          hasFolderScope = true;
        }

        if (folderIds.length > 0) {
          folderIds.forEach((folderId) => {
            if (!folderId || folderId === resolvedWorkspaceId || folderId === selection?.workspaceId) {
              return;
            }
            const folderEntry = index?.nodeById?.[folderId] || null;
            if (!folderEntry?.node) {
              return;
            }
            const folderNode = folderEntry.node;
            const folderTitle = folderNode?.title || folderNode?.name || folderId;
            const targetSegment =
              folderNode?.path && String(folderNode.path).endsWith("/") ? encodePathKey(folderNode.path) : folderId;
            scopeCrumbs.push(
              makeCrumb(
                folderNode,
                folderTitle,
                () => {
                  this.props.setCustomGroup(targetSegment);
                  this.props.setReportGroup(resolvedWorkspaceId);
                  this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${targetSegment}`);
                },
                null,
                "›",
                false,
                true,
              ),
            );
            hasFolderScope = true;
          });
        } else {
          folderPaths = parentFolderPath ? [parentFolderPath] : getReportFolderPaths(reportNode);
          folderPaths.forEach((folderPath) => {
            const parentNode = index?.nodeByPath?.[encodePathKey(folderPath)]?.node || { path: folderPath };
            const parentTitle = parentNode?.title || parentNode?.name || folderPath.split("/").filter(Boolean).slice(-1)[0];
            scopeCrumbs.push(
              makeCrumb(
                parentNode,
                parentTitle,
                () => {
                  const targetSegment = encodePathKey(folderPath);
                  this.props.setCustomGroup(targetSegment);
                  this.props.setReportGroup(resolvedWorkspaceId);
                  this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${targetSegment}`);
                },
                null,
                "›",
                false,
                true,
              ),
            );
            hasFolderScope = true;
          });
        }
      }

      const reportTitle = reportNode.title || reportNode.name || reportId || "Report";
      scopeCrumbs.push(
        makeCrumb(
          reportNode,
          reportTitle,
          () => {
            if (reportId && embedWorkspaceId) {
              this.props.navigate(`/powerBI/report/${embedWorkspaceId}+${reportId}`);
            }
          },
          () => {
            // When reportB was opened from an explicit group click (appendAfterReport),
            // closing it should navigate back to that group — not wipe the whole breadcrumb.
            const selectionGroupId = activeSelection?.groupId || "";
            const selectionFolderPath = activeSelection?.folderPath || "";
            if (appendAfterReport && (selectionGroupId || selectionFolderPath)) {
              if (selectionFolderPath) {
                const targetSegment = encodePathKey(selectionFolderPath);
                this.props.setCustomGroup(targetSegment);
                this.props.setReportGroup(resolvedWorkspaceId);
                this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${targetSegment}`);
              } else {
                this.props.setCustomGroup(selectionGroupId);
                this.props.setReportGroup(resolvedWorkspaceId);
                this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${selectionGroupId}`);
              }
              return;
            }
            if (folderIds.length > 0) {
              const targetId = folderIds[folderIds.length - 1];
              const targetNode = index?.nodeById?.[targetId]?.node || { id: targetId };
              const targetSegment =
                targetNode?.path && String(targetNode.path).endsWith("/") ? encodePathKey(targetNode.path) : targetId;
              this.props.setCustomGroup(targetSegment);
              this.props.setReportGroup(resolvedWorkspaceId);
              this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${targetSegment}`);
            } else {
              const fallbackPath = folderPaths[folderPaths.length - 1] || "";
              if (fallbackPath) {
                const targetSegment = encodePathKey(fallbackPath);
                this.props.setCustomGroup(targetSegment);
                this.props.setReportGroup(resolvedWorkspaceId);
                this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${targetSegment}`);
              } else if (groupIdFromReport) {
                this.props.setCustomGroup(groupIdFromReport);
                this.props.setReportGroup(resolvedWorkspaceId);
                this.props.navigate(`/reports/landing/${resolvedWorkspaceId}/${groupIdFromReport}`);
              } else {
                this.handleDerivedBreadcrumbClose(routeSelection);
              }
            }
          },
          null,
          !suppressActive,
        ),
      );
    }

    // Unshift the anchor report to the very front AFTER reportB is pushed —
    // only when anchor is from a DIFFERENT context: [reportA | groupA > reportB].
    // Same-group siblings are already pushed inline above.

    if (!anchorIsInSameGroup && Boolean(reportId) && anchorReportId && anchorReportId !== reportId && anchorReportNode) {
      const anchorTitle = anchorReportNode.title || anchorReportNode.name || anchorReportId;
      anchorCrumbs.push(
        makeCrumb(
          anchorReportNode,
          anchorTitle,
          () => {
            const anchorWorkspaceId =
              anchorReportNode.workspaceId || anchorReportEntry?.workspace?.workspaceId || embedWorkspaceId || "";
            if (anchorWorkspaceId) {
              this.props.navigate(`/powerBI/report/${anchorWorkspaceId}+${anchorReportId}`);
            }
          },
          null,
          null,
          false,
        ),
      );
    }

    if (scopeCrumbs.length === 0 && anchorCrumbs.length === 0) return null;

    // Render: bare anchor (if any) + scoped chip or bare scope.
    const scopeEl =
      scopeCrumbs.length === 0 ? null : hasFolderScope ? (
        <div className="bc-chip">{scopeCrumbs}</div>
      ) : (
        <div className="bc-bare-group">{scopeCrumbs}</div>
      );

    if (anchorCrumbs.length > 0) {
      return (
        <div className="bc-row">
          <div className="bc-bare-group">{anchorCrumbs}</div>
          {scopeEl}
        </div>
      );
    }
    return scopeEl ? <div className="bc-row">{scopeEl}</div> : null;
  }
  renderSubTabs(selectedTabOverride, routeSelection = {}) {
    const { tabs } = this.props;
    const selectedTab = selectedTabOverride || this.props.selectedTab;
    const routeGroupId = routeSelection?.groupId || "";
    const routeFolderPath = routeSelection?.folderPath || "";
    const routeReportId = routeSelection?.reportId || "";
    const matchesRoute = (item) => {
      if (!item) {
        return false;
      }
      if (routeReportId) {
        const itemId = item.id || item._id || item.rptId || "";
        if (itemId && itemId === routeReportId) {
          return true;
        }
        if (item.id && item.id.includes("+") && item.id.split("+")[1] === routeReportId) {
          return true;
        }
      }
      if (routeGroupId && (item.id === routeGroupId || item._id === routeGroupId)) {
        return true;
      }
      if (routeFolderPath && item.path) {
        return encodePathKey(item.path) === encodePathKey(routeFolderPath);
      }
      return false;
    };

    if (tabs.length !== 0 && selectedTab[0] !== -1) {
      if (!tabs[selectedTab[0]] || tabs[selectedTab[0]].activeChildren === undefined) {
        return;
      }
      return tabs[selectedTab[0]].activeChildren.map((child, idx) => {
        let showDelimiter = true;
        if (idx === tabs[selectedTab[0]].activeChildren.length - 1) {
          showDelimiter = false;
        }
        if (child === undefined || child.title === undefined || child.title === "") {
          return <div key={idx} />;
        }
        let tSelected = selectedTab[1] === idx;
        const routeSelected = matchesRoute(child);

        let justParentSelected = selectedTab[1] === idx && (selectedTab[2] === undefined || selectedTab[2] === -1);

        if (
          child.activeChildren !== undefined &&
          child.activeChildren.length > 0 &&
          (child.type === "grouping" || (child.fileType !== undefined && child.fileType === "folder"))
        ) {
          return (
            <div
              onMouseEnter={(e) => {
                this.setState({ showClose: child.id });
              }}
              onMouseLeave={(e) => {
                this.setState({ showClose: -1 });
              }}
              key={idx}
              className={"renderParent" + idx}
              style={{ display: "flex", flexDirection: "row" }}>
              <div
                style={{
                  paddingLeft: "20px",
                  display: "flex",
                  flexDirection: "row",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                data-testid={this.breadcrumbTestId(child)}
                className={justParentSelected || routeSelected ? "hover topLvlNavDivFind" : "no-hover topLvlNavDivFind"}
                onClick={(evt) => {
                  if (child.index !== selectedTab[1]) {
                    if (child.type === undefined) {
                      this.clickHandler(child, [selectedTab[0], idx], child.fileType, false);
                    } else {
                      this.clickHandler(child, [selectedTab[0], idx], child.type, false);
                    }
                  }
                }}>
                {child.title}
              </div>
              {this.state.showClose === child.id ? (
                <div
                  onClick={(e) => {
                    this.childRemoveHandler(e, child);
                  }}
                  className="close-icon-div">
                  <CloseReportIcon height={"15px"} viewBox={"0 0 20 20"} color={"#58595B"} />
                  <div className="delimiter-style delimiter-hover">{">"}</div>
                </div>
              ) : (
                <div className="delimiter-style delimiter-base">{">"}</div>
              )}
              {this.renderGroupingChilds(child.activeChildren, selectedTab, tSelected, false, routeSelection)}
            </div>
          );
        } else {
          return (
            <div
              onMouseEnter={(e) => {
                this.setState({ showClose: child.id });
              }}
              onMouseLeave={(e) => {
                this.setState({ showClose: -1 });
              }}
              key={idx}
              className="CSD-Stuff"
              style={{ display: "flex", flexDirection: "row" }}>
              <div
                style={{
                  paddingLeft: "20px",
                  display: "flex",
                  flexDirection: "row",
                  cursor: "pointer",
                }}
                data-testid={this.breadcrumbTestId(child)}
                className={tSelected || routeSelected ? "hover child-selected-div" : "child-selected child-selected-div"}
                onClick={(evt) => {
                  if (child.index !== selectedTab[1]) {
                    const clickChild =
                      child.workspaceId === undefined ? { ...child, workspaceId: this.props.activeWS.workspaceId } : child;
                    this.clickHandler(clickChild, [selectedTab[0], idx], clickChild.type, false);
                  }
                }}>
                {child.title}
              </div>
              {this.state.showClose === child.id ? (
                <div
                  onClick={(e) => {
                    this.childRemoveHandler(e, child);
                  }}
                  className="close-icon-div">
                  <CloseReportIcon height={"15px"} viewBox={"0 0 20 20"} color={"#58595B"} />
                  {showDelimiter && <div className="delimiter-style delimiter-hover">|</div>}
                </div>
              ) : (
                this.renderDelimiter(showDelimiter, "|")
              )}
            </div>
          );
        }
      });
    } else {
      return <div />;
    }
  }

  render() {
    const navTopOffset = Number.isFinite(this.props.topOffset) ? this.props.topOffset : 0;
    const { tabs, selectedTab } = this.props;
    const locationPath = this.props.location?.pathname || "";
    const routeWorkspaceId = locationPath.startsWith("/reports/landing/") ? locationPath.split("/")[3] || "" : "";
    let effectiveSelectedTab = selectedTab;
    if (!Array.isArray(effectiveSelectedTab) || effectiveSelectedTab[0] === -1 || !tabs[effectiveSelectedTab[0]]) {
      if (routeWorkspaceId) {
        const routeTabIndex = tabs.findIndex(
          (tab) => tab?.workspaceId === routeWorkspaceId || tab?._id === routeWorkspaceId || tab?.id === routeWorkspaceId,
        );
        if (routeTabIndex !== -1) {
          const nextIndex = Array.isArray(effectiveSelectedTab) ? effectiveSelectedTab[1] : -1;
          effectiveSelectedTab = [routeTabIndex, nextIndex ?? -1];
        } else if (tabs.length === 1) {
          const nextIndex = Array.isArray(effectiveSelectedTab) ? effectiveSelectedTab[1] : -1;
          effectiveSelectedTab = [0, nextIndex ?? -1];
        }
      }
    }
    var showAppHome = false;
    if (tabs.length > 0 && effectiveSelectedTab !== undefined && tabs[effectiveSelectedTab[0]] !== undefined) {
      const tabType = tabs[effectiveSelectedTab[0]].type;
      // Show App Home for regular workspaces (type undefined or "powerBI") and custom apps
      if (tabType === undefined || tabType === "powerBI" || tabType === "customApplication") {
        showAppHome = true;
      }
    }

    const activeWorkspaceId = tabs?.[effectiveSelectedTab?.[0]]?.workspaceId || routeWorkspaceId;
    const appHomePath = activeWorkspaceId ? `/reports/landing/${activeWorkspaceId}` : "";
    const appHomeRouteActive = Boolean(appHomePath) && (locationPath === appHomePath || locationPath === `${appHomePath}/`);
    const routeSelection = getRouteSelection(locationPath);
    const appHomeRouteSelected =
      Boolean(routeSelection.workspaceId) && !routeSelection.groupId && !routeSelection.folderPath && !routeSelection.reportId;
    const hasRouteBreadcrumb = Boolean(routeSelection.groupId || routeSelection.folderPath || routeSelection.reportId);
    const activeChildren = tabs?.[effectiveSelectedTab?.[0]]?.activeChildren;
    const routeBreadcrumbMismatch = hasRouteBreadcrumb && !this.activeChildrenMatchRoute(activeChildren, routeSelection);
    // Custom-app context stack lookup
    const breadcrumbStacks = this.props.breadcrumbStacks || {};
    const allWsForLookup = [...(this.props.workspaces || []), ...tabs];

    /** Find the custom-app workspace object by any of its IDs. */
    const findCustomAppWs = (id) => {
      if (!id) return null;
      return (
        allWsForLookup.find(
          (w) => (w?.type === "customApplication" || (w?.groupings?.length ?? 0) > 0) && (w?._id === id || w?.workspaceId === id),
        ) || null
      );
    };

    /**
     * Look up the breadcrumb stack for a given workspace, trying both _id and
     * workspaceId so that mismatches between componentDidUpdate write-key and
     * render read-key are automatically bridged.
     */
    const lookupStack = (wsId) => {
      if (!wsId) return [];
      if (breadcrumbStacks[wsId]?.length) return breadcrumbStacks[wsId];
      const ws = findCustomAppWs(wsId);
      if (!ws) return [];
      return breadcrumbStacks[ws._id] || breadcrumbStacks[ws.workspaceId] || [];
    };

    // For PowerBI routes, find the owning custom-app workspace by scanning groupings.
    const findCustomAppWsIdForReport = () => {
      for (const ws of allWsForLookup) {
        if ((ws?.type !== "customApplication" && !(ws?.groupings?.length > 0)) || !Array.isArray(ws.groupings)) continue;
        for (const g of ws.groupings) {
          if (
            (g?.children || []).some(
              (c) =>
                c.rptId === routeSelection.reportId || c.id === routeSelection.reportId || c._id === routeSelection.reportId,
            )
          )
            return ws._id || ws.workspaceId;
        }
      }
      return null;
    };
    const customAppWsIdForReport = routeSelection.isPowerBI ? findCustomAppWsIdForReport() : null;

    const activeTabEntry = tabs?.[effectiveSelectedTab?.[0]];
    // A tab may not have type:"customApplication" set (it carries workspaceId only),
    // so also resolve via findCustomAppWs and direct stack-key match.
    const activeTabCustomAppWsId = (() => {
      if (!activeTabEntry) return null;
      if (activeTabEntry.type === "customApplication") return activeTabEntry._id || activeTabEntry.workspaceId;
      const wsId = activeTabEntry.workspaceId || activeTabEntry._id;
      if (!wsId) return null;
      const ws = findCustomAppWs(wsId);
      if (ws) return ws._id || ws.workspaceId;
      // Direct stack-key match — only apply for actual custom apps (type or groupings).
      // PBI workspace tabs now also have stacks; treating them as custom apps here would
      // cause the folder active-state to be computed with an unstripped folderPath.
      const isActuallyCustomAppTab = [...(this.props.workspaces || []), ...tabs].some(
        (w) =>
          (w?._id === wsId || w?.workspaceId === wsId) && (w?.type === "customApplication" || (w?.groupings?.length ?? 0) > 0),
      );
      if (isActuallyCustomAppTab && breadcrumbStacks[wsId]?.length) return wsId;
      return null;
    })();

    // Also resolve via the route workspace (handles app-home routes where
    // the landing URL contains the workspace ID but selectedTab may not yet
    // point to the custom-app tab).
    const routeWsEntry = routeSelection.workspaceId ? findCustomAppWs(routeSelection.workspaceId) : null;
    const routeCustomAppWsId = routeWsEntry ? routeWsEntry._id || routeWsEntry.workspaceId : null;

    // Mirror the CDU guard: when the active tab is a known regular workspace (type falsy
    // or "powerBI") and not resolved as a custom app, don't let findCustomAppWsIdForReport
    // hijack the BC into the custom app stack.  The report GUID may exist in both a
    // workspace and a custom app grouping; the active tab is the authoritative context.
    const activeTabIsRegularWsRender =
      activeTabEntry && !activeTabCustomAppWsId && (!activeTabEntry.type || activeTabEntry.type === "powerBI");

    // When ?appId is present, it was explicitly written by navigation code and is the
    // most authoritative signal — use it first, just like the CDU priority fix. But,
    // exactly as in CDU's routeCustomAppWsId, only trust it once we've verified the
    // report actually belongs to that app — otherwise a stale ?appId (browser back/
    // forward, a hand-edited URL) would point this render at an app whose breadcrumb
    // stack is empty, even though CDU already found and populated the true owner's
    // stack via customAppWsIdForReport below. Falling through here mirrors CDU's own
    // fallback chain so the write side (CDU) and read side (render) stay in sync.
    // resolveValidatedAppWorkspace (shared with CDU) matches on id first and validates
    // that single match — it must NOT keep scanning for a different same-id entry that
    // happens to pass, since workspaces/tabs can hold different data for the same app.
    const urlAppIdRender = new URLSearchParams(this.props.location?.search || "").get("appId") || null;
    const urlAppIdWsRender = this.resolveValidatedAppWorkspace(allWsForLookup, urlAppIdRender, routeSelection.reportId);
    const urlAppIdWsIdRender = urlAppIdWsRender ? urlAppIdWsRender._id || urlAppIdWsRender.workspaceId : null;

    const effectiveCustomAppWsId =
      urlAppIdWsIdRender ||
      (activeTabIsRegularWsRender ? null : customAppWsIdForReport) ||
      activeTabCustomAppWsId ||
      routeCustomAppWsId;
    const isCustomAppRoute = Boolean(effectiveCustomAppWsId);
    // Use cross-ID lookup so _id vs workspaceId mismatches don't return an empty stack.
    const breadcrumbStack = lookupStack(effectiveCustomAppWsId);

    // PowerBI workspace tabs: type === undefined or type === "powerBI" (added via
    // appLanding / homepage app).  MDM (type "mdh"), user-management, and other
    // explicitly typed tabs are excluded.
    const isPowerBIWsTabRender = activeTabEntry && (!activeTabEntry.type || activeTabEntry.type === "powerBI");
    const pbiWsAppId = isPowerBIWsTabRender ? activeTabEntry.workspaceId || activeTabEntry._id || "" : "";
    const isPowerBIWorkspaceRoute = !isCustomAppRoute && isPowerBIWsTabRender && Boolean(pbiWsAppId);
    const pbiWorkspaceBreadcrumbStack = isPowerBIWorkspaceRoute ? breadcrumbStacks[pbiWsAppId] || [] : [];

    let selected = false;
    if (selectedTab[0] === -1) {
      selected = true;
    }

    return (
      <div
        className="ta-tln"
        style={{ position: "fixed", zIndex: 99, width: "100%", top: navTopOffset, pointerEvents: "none" }}
        ref={this.navRef}>
        <div className="ta-top-level-nav-container flex-container">
          <div className="ta-top-level-nav flex-container">
            {!(
              this.props.location.pathname.includes("/uploadFile") || this.props.location.pathname.includes("/tabsFileShare")
            ) && (
              <div
                className={" ta-home-box ta-nav-icons" + (selected ? " ta-home-box-selected" : "")}
                style={{ cursor: "pointer", marginTop: "-4px" }}>
                <Icon tag="i" style={{ color: "white" }} size={20} onClick={this.homeClickHandler} icon={home3} className="home-icon" />
              </div>
            )}
            {this.renderTabs(routeSelection)}
          </div>
        </div>

        <div
          className={"ta-tln-div-child"}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: "42px",
            paddingLeft: "10px",
            paddingRight: "10px",
            overflow: "hidden",
          }}>
          {showAppHome ? (
            <div
              onMouseEnter={(e) => {
                this.setState({ showClose: "home" });
              }}
              onMouseLeave={(e) => {
                this.setState({ showClose: -1 });
              }}
              style={{ display: "flex", flexDirection: "row", alignItems: "center", flexShrink: 0 }}>
              <div
                onClick={() => {
                  this.props.setSelectedTab([selectedTab[0], -1]);
                  this.props.setCustomGroup("");
                  this.props.setReportGroup(this.props.tabs[selectedTab[0]].workspaceId);
                  this.props.navigate("/reports/landing/" + this.props.tabs[selectedTab[0]].workspaceId);
                }}
                style={{ display: "flex", alignItems: "center", gap: "4px", position: "relative" }}
                data-testid="breadcrumb-item-app-home"
                className={"app-home " + (appHomeRouteActive || appHomeRouteSelected ? "hover" : "")}>
                {this.state.showClose === "home" ? (
                  <span
                    className="bc-close-btn"
                    title="Close"
                    style={{ display: "inline-flex", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      let workspaceId;
                      const closingTab = this.props.tabs[this.props.selectedTab[0]];
                      // Guard: selectedTab may be stale (e.g. [-1,-1] after navigating home
                      // and then clicking "more..." on a workspace) making closingTab undefined.
                      // In that case just navigate away without trying to remove a tab.
                      if (!closingTab) {
                        this.props.setCustomGroup("");
                        this.props.setSelectedTab([-1, -1]);
                        this.props.setReportGroup("");
                        this.props.navigate(this.props.defaultApp?.link || "/");
                        return;
                      }
                      const closingWorkspaceId = closingTab?.workspaceId || closingTab?._id || "";
                      if (closingWorkspaceId) {
                        this.props.clearBreadcrumbSelection(closingWorkspaceId);
                      }
                      this.props.removeTabFromNav(closingTab, 1);

                      this.props.setCustomGroup("");
                      if (this.props.tabs.length === 1) {
                        this.props.setSelectedTab([-1, -1]);
                        this.props.setReportGroup("");
                        this.props.navigate(this.props.defaultApp.link);
                      } else {
                        if (this.props.tabs.length > 1 && this.props.selectedTab[0] > 0) {
                          if (this.props.tabs[this.props.selectedTab[0] - 1].workspaceId === undefined) {
                            this.props.setReportGroup("");
                            this.props.navigate(this.props.tabs[this.props.selectedTab[0] - 1].path);
                          } else {
                            workspaceId = this.props.tabs[this.props.selectedTab[0] - 1].workspaceId;
                            this.props.setReportGroup(workspaceId);

                            this.props.navigate("/reports/landing/" + workspaceId);
                          }
                          this.props.setSelectedTab([this.props.selectedTab[0] - 1, -1]);
                        } else {
                          if (this.props.tabs[this.props.selectedTab[0] + 1].workspaceId === undefined) {
                            this.props.setReportGroup("");
                            this.props.navigate(this.props.tabs[this.props.selectedTab[0] + 1].path);
                          } else {
                            workspaceId = this.props.tabs[this.props.selectedTab[0] + 1].workspaceId;
                            this.props.setReportGroup(workspaceId);

                            this.props.navigate("/reports/landing/" + workspaceId);
                          }
                          this.props.setSelectedTab([this.props.selectedTab[0], -1]);
                        }
                      }
                    }}>
                    <CloseReportIcon height={"13px"} viewBox={"0 0 20 20"} color={"#58595B"} />
                  </span>
                ) : (
                  <BcHomeIcon />
                )}
                App Home
              </div>
              <div className="delimiter-style bc-delim-chevron" style={{ marginLeft: "6px", marginRight: "2px" }}>
                ›
              </div>
            </div>
          ) : null}
          {(() => {
            // Suppress BC content while workspaces are still loading and the URL points to
            // a folder, group, or report.  Without workspace data neither isCustomAppRoute
            // nor isPowerBIWorkspaceRoute will be true yet, so the fallback paths produce
            // the wrong "WorkspaceName > Folder" output.  Once workspaces arrive,
            // componentDidUpdate pushes the correct stack items and this re-renders with
            // the proper "App Home > Folder" hierarchy.
            if ((this.props.workspaces?.length ?? 0) === 0 && hasRouteBreadcrumb) {
              return null;
            }
            if (isCustomAppRoute) {
              // Pass the current route's item ID so the correct crumb is highlighted.
              // Empty string when on App Home (no crumb active).
              const activeStackItemId = appHomeRouteSelected
                ? ""
                : routeSelection.reportId || routeSelection.groupId || routeSelection.folderPath || "";
              return this.renderBreadcrumbStack(breadcrumbStack, effectiveCustomAppWsId, activeStackItemId);
            }
            if (isPowerBIWorkspaceRoute) {
              // Normalise folder path (strip trailing slash) for active-item matching.
              const normFp = routeSelection.folderPath ? routeSelection.folderPath.replace(/\/+$/, "") : "";
              const activeWsItemId = appHomeRouteSelected
                ? ""
                : routeSelection.reportId || normFp || routeSelection.groupId || "";
              return this.renderBreadcrumbStack(pbiWorkspaceBreadcrumbStack, pbiWsAppId, activeWsItemId);
            }
            if (routeSelection.reportId || (hasRouteBreadcrumb && routeBreadcrumbMismatch)) {
              return this.renderDerivedSubTabs(routeSelection);
            }
            const subTabs = this.renderSubTabs(effectiveSelectedTab, routeSelection);
            const hasSubTabs = Array.isArray(subTabs) ? subTabs.length > 0 : subTabs && subTabs.props?.children !== undefined;
            return hasSubTabs ? subTabs : this.renderDerivedSubTabs(routeSelection);
          })()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedTab: state.topLevelNav.selectedTab,
  tabs: state.topLevelNav.tabs,
  searchTabs: state.topLevelNav.searchTabs,
  defaultApp: state.app.defaultApp,
  homePage: state.app.homePage,
  activeWS: state.reportList.activeWS,
  workspaces: state.reportList.workspaces,
  reportGroup: state.app.reportGroup,
  customGroup: state.app.customGroup,
  apps: state.app.apps,
  inboxId: state.app.inboxId,
  breadcrumbStacks: state.breadcrumb?.stackByWorkspace || {},
});
const mapDispatchToProps = (dispatch) => ({
  setSelectedTab(index) {
    dispatch(setSelectedTab(index));
  },
  addTabToNav(tab) {
    dispatch(addTabToNav(tab));
  },
  removeTabFromNav(tab) {
    dispatch(removeTabFromNav(tab));
  },
  removeChild(tab, child) {
    dispatch(removeChild(tab, child));
  },
  setReportGroup(group) {
    dispatch(setReportGroup(group));
  },
  filterTab(filter, tabs, selectedTab) {
    dispatch(filterTab(filter, tabs, selectedTab));
  },
  setCustomGroup(group) {
    dispatch(setCustomGroup(group));
  },
  setBreadcrumbSelection(workspaceId, selection) {
    dispatch(setBreadcrumbSelection(workspaceId, selection));
  },
  clearBreadcrumbSelection(workspaceId) {
    dispatch(clearBreadcrumbSelection(workspaceId));
  },
  clearFileSearch() {
    dispatch(clearFileSearch());
  },
  pushBreadcrumbItem(wsId, item) {
    dispatch(pushBreadcrumbItem(wsId, item));
  },
  insertBreadcrumbItemAfter(wsId, item, afterId) {
    dispatch(insertBreadcrumbItemAfter(wsId, item, afterId));
  },
  removeBreadcrumbItem(wsId, itemId) {
    dispatch(removeBreadcrumbItem(wsId, itemId));
  },
  popBreadcrumbToId(wsId, itemId) {
    dispatch(popBreadcrumbToId(wsId, itemId));
  },
  clearBreadcrumbStack(wsId) {
    dispatch(clearBreadcrumbStack(wsId));
  },
  setReportAppStatus(status) {
    dispatch(setReportAppStatus(status));
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TopLevelNav));
